import { isReviewRequestLookupCommand } from './workflow-review-request-lookup-authoring-gate';
import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { providesVotingBlock } from '@app/shared/workflow-voting/voting-sources';
import {
  initializeBlankStep,
  StepAuthoringCatalog,
  StepCommand,
} from './workflow-step-composition';
import { WorkflowStepComponentsEditorComponent } from './workflow-step-components-editor.component';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { MatDialog, MatDialogState } from '@angular/material/dialog';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { WorkflowLibrarySelectorComponent } from './workflow-library-selector.component';
import {
  WorkflowLibraryCategory,
  WorkflowLibraryClosedReason,
  WorkflowLibraryEntry,
} from './workflow-library-selector.types';
import { firstValueFrom } from 'rxjs';
import {
  BlockManifest,
  BlockRegion,
  parseBlockSettings,
} from '@template-ui/blocks';
import { createWorkflowBlockRegistry } from '@app/shared/workflow-blocks/workflow-blocks';
import {
  domainBlockCodes,
  isBlockOwnedCommand,
  sdmfCommandCodes,
  domainBlockManifests,
  sourceContractVersion,
  noticeBlockCodes,
  noticeCommandCodes,
  headerHistoryBlockManifests,
} from '@app/phms/workflow-blocks/domain-block-manifests';
import { NoticePolicyEditorComponent, type NoticePolicyChangeRequest } from '@app/phms/workflow-blocks/notice-policy-editor.component';
import type { WorkflowCompletionRequirement } from '@app/shared/workflow-commands/workflow-completion-requirements';
import { WorkflowDraftConfirmationService } from './workflow-draft-confirmation.service';
import { processDataErrors } from './workflow-process-data';
import { resizableSchedulingBlockManifests, schedulingBlockManifests } from '@app/phms/workflow-blocks/scheduling-block-manifests';
import { enablePageTabs, setPageTabs } from './workflow-page-tabs';
import { WorkflowVotingConfigurationComponent } from './workflow-voting-configuration.component';
import { removeVoting } from './workflow-voting-authoring';
import {
  defaultNoticePolicy,
  NOTICE_REQUIREMENT,
  parseNoticePolicy,
  noticeRequirementIssue,
  noticePolicyDescription,
} from '@app/phms/workflow-blocks/notice-policy';
import {
  changeDomainSource,
  compatibleHearingFields,
  requireSdmf,
  sourceSettings,
  changeNoticePolicy,
  ensureNoticeGroup,
} from './workflow-domain-block-authoring';
import {
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
  WorkflowStepDeclaration,
  WorkflowCapabilityReference,
  requireWorkflowPayloadVersion,
} from './platform-workflow-definition-lifecycle.models';
import { PlatformWorkflowDefinitionLifecycleService } from './platform-workflow-definition-lifecycle.service';
import {
  addWorkflowCapability,
  capabilityIdentity,
  sameCapabilityReference,
  installedReference,
  WorkflowCapabilityCatalogEntry,
} from './workflow-capability-authoring';
import { replacePresentation } from './workflow-presentation-authoring';

import { WorkflowPresentationPreviewComponent } from './workflow-presentation-preview.component';
import { WorkflowCaptureFormEditorComponent } from './workflow-capture-form-editor.component';
import { WorkflowFormFieldsEditorComponent } from './workflow-form-fields-editor.component';
import {
  captureFieldTypes,
  captureFormIssues,
  isUnsupportedCaptureForm,
} from './workflow-capture-form-validation';
import {
  workflowFormFieldIssues,
  isUnsupportedFormConfiguration,
} from './workflow-form-fields-validation';
import { reviewRequestLookupSettingsAuthoringReason, preparationLookupSettingsAuthoringReason, providedFormSettingsAuthoringReason, providedFormSettingsShapeReason } from './workflow-provided-lookup-authoring';
import { isPreparationLookupCommand } from './workflow-preparation-lookup-authoring-gate';
import {
  WorkflowCaptureField,
  WorkflowCaptureForm,
} from '@app/shared/workflow-commands/workflow-capture-form';
import { WorkflowFormConfiguration } from '@app/shared/workflow-commands/workflow-form-configuration';
import { isWorkflowFormVersionAvailable } from '@app/shared/workflow-commands/workflow-form-layout';
import { workflowCommandIdentity } from '@app/shared/workflow-commands/workflow-command-registry';
import { workflowCompleteCommandAdapter } from '@app/shared/workflow-commands/workflow-complete-command-adapter';
import {
  DetailConfigureTarget,
  DetailFieldOrder,
  DetailBlockMove,
} from '@app/workflow-workspace/workflow-workspace-authoring';
import {
  blockCapabilityReason,
  blockOwnedCommandCodes,
  blockOwnedCommandVersion,
  preferredBlockCatalog,
} from './workflow-block-catalog';
import {
  WorkflowOwnerViewState,
  WorkflowOwnerFocus,
  captureWorkflowOwnerFocus,
  restoreWorkflowOwnerFocus,
} from './workflow-owner-view-state';
import type {
  WorkflowEditorBufferState,
  WorkflowEditorPendingScope,
} from './workflow-editor-pending-state';

type Detail = WorkflowStepDeclaration['detail'];
type PlacedBlock = Detail['blocks'][number];
interface DetailSelectionOption {
  label: string;
  target: DetailConfigureTarget;
}
interface DetailSelectionParent extends DetailSelectionOption {
  type: string;
}
interface DetailUndo {
  document: WorkflowDefinitionDocument | null;
  detail: Detail;
  selectedId: string;
  selection: DetailConfigureTarget | null;
  commands: StepCommand[];
  mobileRegion: BlockRegion;
  label: string;
}
type ComposerTextTarget =
  | { kind: 'form-title'; command: string }
  | {
      kind: 'capture-label' | 'installed-label';
      command: string;
      field: string;
    }
  | { kind: 'block'; id: string; field: string };
interface ComposerTextDraft {
  target: ComposerTextTarget;
  workflowCode: string;
  definitionVersionCode: string;
  packReference: string;
  nodeId: string;
  pin: string;
  original: string | undefined;
  value: string;
  error: string;
  pageSettingsNamespace?: string;
}
export interface WorkflowPageSettingsOrigin {
  generation: number; nodeId: string; owner: string; source: string; view: string | null; selection: string;
}
export interface WorkflowNoticeSettingsOrigin extends WorkflowPageSettingsOrigin { blockId: string | null; group: string; task: boolean; }
export interface WorkflowNoticeRequirementRequest {
  nodeId: string; blockId: string; noticeGroupId: string; requirementId: string;
  owner: string; source: string; view: string; context: string; requirement: WorkflowCompletionRequirement;
}
interface NoticeView {
  document: WorkflowDefinitionDocument; node: WorkflowDefinitionNode; block: PlacedBlock;
  blocks: PlacedBlock[]; notices: PlacedBlock[]; commands: StepCommand[];
  requirements: WorkflowCompletionRequirement[]; pins: WorkflowCapabilityReference[];
}
const noticeRecord = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
interface ContentLibraryLease {
  origin: WorkflowPageSettingsOrigin;
  region: BlockRegion;
  tabId: string | undefined;
  resetVersion: number;
  capabilities: WorkflowCapabilityCatalogEntry[];
  request: number;
}
interface ContentLibraryFocus {
  origin: WorkflowPageSettingsOrigin;
  namespace: string;
  resetVersion: number;
  request: number;
  before: Element | null;
}
interface ComposerTextResolution {
  value: string | undefined;
  pin: string;
  label: string;
  validate: (value: string) => string;
  patch: (value: string) => WorkflowDefinitionDocument;
}
@Component({
  selector: 'phms-workflow-detail-composer',
  standalone: true,
  imports: [
    WorkflowVotingConfigurationComponent,
    UiButtonDirective,
    UiControlDirective,
    CommonModule,
    FormsModule,
    DragDropModule,
    WorkflowLibrarySelectorComponent,
    WorkflowPresentationPreviewComponent,
    WorkflowCaptureFormEditorComponent,
    WorkflowFormFieldsEditorComponent,
    WorkflowStepComponentsEditorComponent,
    NoticePolicyEditorComponent,
  ],
  templateUrl: './workflow-detail-composer.component.html',
  styleUrl: './workflow-detail-composer.component.scss',
})
export class WorkflowDetailComposerComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) document!: WorkflowDefinitionDocument;
  @Input({ required: true }) node!: WorkflowDefinitionNode;
  @Input() disabled = false;
  @Input() active = true;
  @Input() guardPageSettings = false;
  @Input() guardContentLibrary = false;
  @Input() guardNoticeSettings = false;
  @Input() latestDocument: (() => WorkflowDefinitionDocument | null) | null = null;
  @Input() ownerContext: (() => unknown) | null = null;
  @Input() formViewContext: (() => unknown) | null = null;
  @Input() pageSettingsView: (() => string | null) | null = null;
  @Input() pageSettingsViewState: string | null | undefined = undefined;
  @Input() workEditorAvailable = false;
  @Input() componentCatalog: StepAuthoringCatalog | null = null;
  @Input() componentCapabilities: WorkflowCapabilityCatalogEntry[] = [];
  @Input() resetVersion = 0;
  @Input() focusAction: {
    capability: StepCommand['capability'];
    request: number;
    outcomes?: boolean;
  } | null = null;
  private lastFocusAction: typeof this.focusAction = null;
  @Input() focusForm: {
    capability: StepCommand['capability'];
    request: number;
  } | null = null;
  private lastFocusForm: typeof this.focusForm = null;
  @Input() focusBlock: { id: string } | null = null;
  private lastFocusBlock: { id: string } | null = null;
  @ViewChild('properties') private properties?: ElementRef<HTMLElement>;
  @Output() readonly documentChange =
    new EventEmitter<WorkflowDefinitionDocument>();
  @Output() readonly pendingChangesChange = new EventEmitter<boolean>();
  @Output() readonly preview = new EventEmitter<void>();
  @Output() readonly openOutcomes = new EventEmitter<StepCommand>();
  @Output() readonly openForm = new EventEmitter<StepCommand>();
  @Output() readonly openActions = new EventEmitter<StepCommand | undefined>();
  @Output() readonly noticeRequirementRequested = new EventEmitter<WorkflowNoticeRequirementRequest>();
  readonly registry = createWorkflowBlockRegistry();
  readonly regions: BlockRegion[] = ['main', 'supporting'];
  private readonly service = inject(PlatformWorkflowDefinitionLifecycleService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly confirmation = inject(WorkflowDraftConfirmationService);
  private readonly noticeDialogs = inject(MatDialog);
  @ViewChild(NoticePolicyEditorComponent) private noticePolicyEditor?: NoticePolicyEditorComponent;
  noticeSettingsOpen = false;
  private noticeSettingsReturn: { namespace: string; nodeId: string; blockId: string; tabId: string | null; origin: HTMLElement | null } | null = null;
  private noticeRefusal: { kind: 'remove' | 'read-only'; message: string } | null = null;
  private noticeRecoveryRequest = 0;
  private noticeRecoveryPending = false;
  private noticeRequirementEmission: WorkflowNoticeRequirementRequest | null = null;
  private noticeRequirementOrigin: WorkflowNoticeSettingsOrigin | null = null;
  private noticeRequirementAfterDialog = false;
  private noticeEnableOrigin: WorkflowNoticeSettingsOrigin | null = null;
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true }) ?? new WorkflowOwnerViewState();
  private ownerViewKey = '';
  private ownerFocus: WorkflowOwnerFocus | null = null;
  private ownerFocusSelection = '';
  private ownerFocusRequest = 0;
  private ownerRestorePending = false;
  private ownerActive = true;
  private pageSettingsGeneration = 0;
  private pageSettingsRenderedView: string | null = null;
  private pageSettingsAuthority = '';
  private pageSettingsDestroyed = false;
  private restoringPageSettings = false;
  private pageSettingsDocument(): WorkflowDefinitionDocument | null { return this.latestDocument ? this.latestDocument() : this.document; }
  private pageSettingsOwner(): string { return JSON.stringify(this.ownerContext?.() ?? [this.document?.workflowCode, this.document?.definitionVersionCode]); }
  private pageSettingsNamespace(): string { return JSON.stringify([this.pageSettingsOwner(), this.formViewContext?.() ?? null, this.node?.id]); }
  private pageSettingsShapeReason(): string {
    const detail = this.detail;
    const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
    if (!record(detail) || detail.layout !== 'workspace.two-column.v1' || !['side', 'below', 'hidden'].includes(detail.contextPlacement)
      || !Array.isArray(detail.blocks) || detail.blocks.some(block => !record(block) || typeof block.instanceId !== 'string' || !block.instanceId.trim()
        || !['main', 'supporting'].includes(block.region)))
      return 'Saved page settings use an unavailable format. Their original values are retained for inspection.';
    if (new Set(detail.blocks.map(block => block.instanceId)).size !== detail.blocks.length)
      return 'Saved content identities are ambiguous. Review the original settings before changing this page.';
    const tabs = detail.tabs;
    if (tabs != null && (!Array.isArray(tabs) || tabs.length > 8 || tabs.some(tab => !record(tab)
      || Object.keys(tab).some(key => !['id', 'label'].includes(key)) || typeof tab.id !== 'string' || !tab.id.trim()
      || typeof tab.label !== 'string' || !tab.label.trim() || tab.label !== tab.label.trim() || tab.label.length > 80)
      || new Set(tabs.map(tab => tab.id)).size !== tabs.length || new Set(tabs.map(tab => tab.label.toLowerCase())).size !== tabs.length))
      return 'Saved page tabs are unavailable or ambiguous. Their original titles and membership are retained for inspection.';
    if (detail.formTabId != null && (typeof detail.formTabId !== 'string' || !tabs?.some(tab => tab.id === detail.formTabId))
      || detail.blocks.some(block => block.tabId != null && (block.region !== 'main' || typeof block.tabId !== 'string' || !tabs?.some(tab => tab.id === block.tabId))))
      return 'Saved page-tab membership is unavailable. Its original content is retained for inspection.';
    return '';
  }
  get pageSettingsState(): {
    origin: WorkflowPageSettingsOrigin; unavailableReason: string; referencePlacementReason: string;
    blockingReferences: readonly { instanceId: string; label: string; available: boolean; reason: string }[];
  } {
    const blocks = Array.isArray(this.detail?.blocks) ? this.detail.blocks : [];
    const blockingReferences = blocks.filter(block => block?.region === 'supporting').map(block => {
      const available = typeof block.instanceId === 'string' && !!block.instanceId.trim() && blocks.filter(item => item?.instanceId === block.instanceId).length === 1;
      return { instanceId: block.instanceId, label: this.name(block), available,
        reason: available ? '' : 'This saved content identity is unavailable or ambiguous.' };
    });
    return { origin: { generation: this.pageSettingsGeneration, nodeId: this.node?.id, owner: this.pageSettingsOwner(), source: JSON.stringify(this.pageSettingsDocument()),
      view: this.pageSettingsView?.() ?? null, selection: JSON.stringify(this.selection) }, unavailableReason: this.pageSettingsShapeReason(),
      referencePlacementReason: blockingReferences.length ? 'Hidden is unavailable while reference content remains. Inspect the content below to move or remove it deliberately.' : '', blockingReferences };
  }
  private pageSettingsCurrent(origin: WorkflowPageSettingsOrigin | undefined, mutation: boolean): boolean {
    if (!this.guardPageSettings) return true;
    const document = this.pageSettingsDocument(), view = this.pageSettingsView?.() ?? null;
    return !this.pageSettingsDestroyed && !this.restoringPageSettings && !!origin && !!document && this.active && !this.loading
      && (!mutation || !this.disabled && this.mode === 'edit' && !this.pageSettingsShapeReason()
        && (this.selection?.kind === 'tabs' || this.selection?.kind === 'context'))
      && !!view && view === this.pageSettingsRenderedView && (this.pageSettingsViewState === undefined || this.pageSettingsViewState === view)
      && this.pageSettingsAuthority === this.pageSettingsNamespace() && origin.generation === this.pageSettingsGeneration
      && origin.owner === this.pageSettingsOwner() && origin.nodeId === this.node.id && origin.view === view
      && origin.source === JSON.stringify(document) && origin.source === JSON.stringify(this.document) && origin.selection === JSON.stringify(this.selection)
      && Array.isArray(document.nodes) && document.nodes.filter(node => node?.id === this.node.id && node.typeCode === 'human_step').length === 1
      && document.nodes.filter(node => node?.id === this.node.id).length === 1
      && JSON.stringify(document.nodes.filter(node => node?.id === this.node.id)[0]) === JSON.stringify(this.node)
      && (!this.textDraft || this.textDraft.pageSettingsNamespace === this.pageSettingsNamespace())
      && !this.ownerViews.focusSuspended() && !this.host.nativeElement.closest('[hidden],[inert],[aria-hidden="true"]');
  }
  private restorePageSettingsControl(model: NgModel | undefined, value: unknown): void {
    if (!model || this.restoringPageSettings) return;
    this.restoringPageSettings = true; try { model.control.setValue(value, { emitEvent: false }); } finally { this.restoringPageSettings = false; }
  }
  openPageSettings(origin?: WorkflowPageSettingsOrigin): void {
    if (!this.pageSettingsCurrent(origin, false)) return;
    if (!this.configure({ kind: 'tabs' })) return;
    this.focusPageSettingsSelection();
  }
  inspectReferenceContent(instanceId: string, origin?: WorkflowPageSettingsOrigin): void {
    if (!this.pageSettingsCurrent(origin, false)) return;
    const blocks = Array.isArray(this.detail.blocks) ? this.detail.blocks.filter(block => block?.instanceId === instanceId) : [];
    if (blocks.length !== 1 || blocks[0].region !== 'supporting') { this.error = 'This exact reference content is missing or ambiguous. Its saved values are retained.'; return; }
    if (this.configure({ kind: 'block', id: instanceId })) this.focusPageSettingsSelection();
  }
  private focusPageSettingsSelection(): void {
    const request = ++this.ownerFocusRequest, origin = this.pageSettingsState.origin, before = this.host.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.host.nativeElement, active = host.ownerDocument.activeElement;
      if (request !== this.ownerFocusRequest || !host.isConnected || !this.pageSettingsCurrent(origin, false)) return;
      if (active !== before && active !== host.ownerDocument.body && active?.isConnected && !active.closest('[hidden],[inert]')) return;
      const heading = this.properties?.nativeElement.querySelector<HTMLElement>('.selection-context__name');
      if (!heading?.isConnected || heading.closest('[hidden],[inert]')) return;
      heading.focus({ preventScroll: true });
      if (host.ownerDocument.activeElement === heading && this.ownerFocusRequest === request + 1 && this.pageSettingsCurrent(origin, false))
        heading.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }

  @HostListener('focusin', ['$event'])
  rememberOwnerFocus(event: FocusEvent): void {
    this.ownerFocusRequest++;
    this.ownerFocus = captureWorkflowOwnerFocus(this.host.nativeElement, event.target);
    this.ownerFocusSelection = JSON.stringify(this.selection);
  }

  private rememberOwnerView(): void {
    if (!this.ownerViewKey || this.ownerRestorePending) return;
    const selection = this.selection;
    const command = selection && 'command' in selection
      ? this.currentDocument?.nodes.find(node => node.id === this.currentNode)?.declaration?.behavior.commands
        .find(item => this.formIdentity(item) === selection.command)
      : undefined;
    const block = selection?.kind === 'block'
      ? this.currentNodeValue?.declaration?.detail.blocks.find(item => item.instanceId === selection.id)
      : undefined;
    this.ownerViews.remember(this.ownerViewKey, {
      selection: this.selection,
      selectedId: this.selectedId,
      blockPin: block ? JSON.stringify([block.provider, block.code, block.version, block.settingsVersion]) : undefined,
      commandPin: command?.capability,
      mobilePanel: this.mobilePanel,
      inspectorOpen: this.inspectorOpen,
      mobileRegion: this.mobileRegion,
      previewTabId: this.previewTabId,
      canvasSize: this.canvasSize,
      focus: this.ownerFocus,
      focusSelection: this.ownerFocusSelection,
    });
  }

  private restoreOwnerView(): void {
    const retained = this.ownerViews.read<{
      selection: DetailConfigureTarget | null;
      selectedId: string;
      blockPin?: string;
      commandPin?: StepCommand['capability'];
      mobilePanel: 'page' | 'settings';
      inspectorOpen: boolean;
      mobileRegion: BlockRegion;
      previewTabId: string | null;
      canvasSize: 'available' | 'phone';
      focus: WorkflowOwnerFocus | null;
      focusSelection: string;
    }>(this.ownerViewKey);
    this.ownerRestorePending = false;
    if (!retained?.selection || retained.selection.kind === 'add-block' || retained.selection.kind === 'action-library') return;
    const selection = retained.selection;
    const unavailable = () => {
      this.error = 'The previously selected Staff page item is unavailable or ambiguous. Select an item to inspect its settings.';
    };
    if (selection.kind === 'block') {
      const blocks = this.blocks.filter(block => block.instanceId === selection.id);
      if (blocks.length !== 1 || retained.blockPin !== JSON.stringify([blocks[0].provider, blocks[0].code, blocks[0].version, blocks[0].settingsVersion])) {
        unavailable();
        return;
      }
    }
    if ('command' in selection) {
      const matches = this.node.declaration?.behavior.commands.filter(command => this.formIdentity(command) === selection.command) ?? [];
      if (matches.length !== 1 || !retained.commandPin || !sameCapabilityReference(matches[0].capability, retained.commandPin)) {
        unavailable();
        return;
      }
      if (!this.componentCatalog) {
        this.ownerRestorePending = true;
        return;
      }
      if (selection.kind === 'field' && this.selectionOptions.filter(option =>
        option.target.kind === 'field' && option.target.command === selection.command && option.target.id === selection.id).length !== 1) {
        unavailable();
        return;
      }
    }
    this.selection = selection;
    this.selectionChosen = true;
    this.selectedId = retained.selectedId;
    this.mobilePanel = retained.mobilePanel;
    this.inspectorOpen = retained.inspectorOpen;
    this.mobileRegion = retained.mobileRegion;
    this.canvasSize = retained.canvasSize;
    this.previewTabId = this.detail.tabs?.filter(tab => tab.id === retained.previewTabId).length === 1 ? retained.previewTabId : null;
    this.ownerFocus = retained.focus;
    this.ownerFocusSelection = retained.focusSelection;
  }

  private restoreOwnerFocus(): void {
    const request = ++this.ownerFocusRequest;
    const key = this.ownerViewKey;
    const selection = JSON.stringify(this.selection);
    setTimeout(() => {
      if (this.ownerViews.focusSuspended() || request !== this.ownerFocusRequest || !this.active || key !== this.ownerViewKey || selection !== JSON.stringify(this.selection)) return;
      if (selection === this.ownerFocusSelection && restoreWorkflowOwnerFocus(this.host.nativeElement, this.ownerFocus)) return;
      if (!this.host.nativeElement.closest('[hidden], [inert]'))
        this.properties?.nativeElement.querySelector<HTMLElement>('.selection-context__name')?.focus({ preventScroll: true });
    });
  }

  ngOnDestroy(): void {
    this.ownerFocusRequest++;
    this.pageSettingsDestroyed = true; this.pageSettingsGeneration++;
    this.noticeRecoveryRequest++;
    this.rememberOwnerView();
  }
  @ViewChild('contentLibrary')
  private contentLibrary?: WorkflowLibrarySelectorComponent;
  private contentLibraryLease: ContentLibraryLease | null = null;
  private contentLibraryDialogOpen = false;
  private contentLibraryRequest = 0;
  private contentLibraryAdding: ContentLibraryLease | null = null;
  private contentLibraryFocus: ContentLibraryFocus | null = null;
  private contentLibraryRows: {
    entry: WorkflowLibraryEntry;
    manifest: BlockManifest;
  }[] = [];
  @ViewChild(WorkflowCaptureFormEditorComponent)
  private captureEditor?: WorkflowCaptureFormEditorComponent;
  readonly fieldTypes = captureFieldTypes;
  selection: DetailConfigureTarget | null = null;
  private selectionChosen = false;
  mode: 'edit' | 'try' = 'edit';
  mobilePanel: 'page' | 'settings' = 'page';
  inspectorOpen = true;
  canvasSize: 'available' | 'phone' = 'available';
  canvasWidth = 0;
  readonly trackTab = (_index: number, tab: { id: string }) => tab.id;
  previewTabId: string | null = null;
  private targetTabId?: string;
  enableTabs(origin?: WorkflowPageSettingsOrigin): void {
    if (this.pageSettingsCurrent(origin, true) && this.prepareTextChange()) this.change(enablePageTabs(this.detail), 'Enable page tabs');
  }
  editTabs(tabs: NonNullable<Detail['tabs']>, origin?: WorkflowPageSettingsOrigin): void {
    if (!this.pageSettingsCurrent(origin, true) || !this.prepareTextChange()) return;
    try { this.change(setPageTabs(this.detail, tabs), 'Change page tabs'); }
    catch (error) { this.error = error instanceof Error ? error.message : 'Invalid page tabs.'; }
  }
  addTab(origin?: WorkflowPageSettingsOrigin): void {
    if (!this.pageSettingsCurrent(origin, true)) return;
    const tabs = this.detail.tabs ?? [];
    let number = tabs.length + 1;
    while (tabs.some(tab => tab.label === `Tab ${number}`)) number++;
    this.editTabs([...tabs, { id: `tab-${crypto.randomUUID()}`, label: `Tab ${number}` }], origin);
  }
  renameTab(id: string, control: HTMLInputElement, origin?: WorkflowPageSettingsOrigin): void {
    const tabs = Array.isArray(this.detail.tabs) ? this.detail.tabs : [];
    if (this.pageSettingsCurrent(origin, true) && tabs.filter(tab => tab.id === id).length === 1)
      this.editTabs(tabs.map(tab => tab.id === id ? { ...tab, label: control.value.trim() } : tab), origin);
    control.value = (Array.isArray(this.detail.tabs) ? this.detail.tabs : []).filter(tab => tab.id === id)[0]?.label ?? '';
  }
  moveTab(index: number, delta: number, origin?: WorkflowPageSettingsOrigin, tabId?: string): void {
    if (this.guardPageSettings && (!this.pageSettingsCurrent(origin, true) || !Array.isArray(this.detail.tabs)
      || this.detail.tabs[index]?.id !== tabId || this.detail.tabs.filter(tab => tab.id === tabId).length !== 1)) return;
    const tabs = [...this.detail.tabs ?? []], destination = index + delta;
    if (destination < 0 || destination >= tabs.length) return;
    [tabs[index], tabs[destination]] = [tabs[destination], tabs[index]];
    this.editTabs(tabs, origin);
  }
  removeTab(id: string, origin?: WorkflowPageSettingsOrigin): void {
    if (this.guardPageSettings && (!this.pageSettingsCurrent(origin, true) || !Array.isArray(this.detail.tabs) || this.detail.tabs.filter(tab => tab.id === id).length !== 1)) return;
    this.editTabs((this.detail.tabs ?? []).filter(tab => tab.id !== id), origin);
  }
  setBlockTab(id: string): void {
    if (this.selected?.region === 'main' && this.detail.tabs?.some(tab => tab.id === id))
      this.update({ ...this.selected, tabId: id }, 'Move block to tab');
  }
  setFormTab(id: string, origin?: WorkflowPageSettingsOrigin, model?: NgModel): void {
    if (this.pageSettingsCurrent(origin, true) && this.prepareTextChange() && this.detail.tabs?.filter(tab => tab.id === id).length === 1)
      this.change({ ...this.detail, formTabId: id }, 'Move task form to tab');
    this.restorePageSettingsControl(model, this.detail.formTabId || (Array.isArray(this.detail.tabs) ? this.detail.tabs[0]?.id : undefined));
  }
  issuesOpen = false;
  selectedId = '';
  libraryOpen = false;
  showUnavailable = false;
  catalogLoading = false;
  catalogError = '';
  private fetchedCapabilities: WorkflowCapabilityCatalogEntry[] | null = null;
  private capabilitiesInput: WorkflowCapabilityCatalogEntry[] | null = null;
  private catalogRequest: Promise<WorkflowCapabilityCatalogEntry[]> | null =
    null;
  private catalogScope = '';
  private catalogRequestScope = '';
  private catalogRequestInput: WorkflowCapabilityCatalogEntry[] | null = null;
  private catalogRequestReset = -1;
  private catalogRequestLease: ContentLibraryLease | null = null;
  private catalogCache: {
    document: WorkflowDefinitionDocument;
    node: WorkflowDefinitionNode;
    capabilities: WorkflowCapabilityCatalogEntry[] | null;
    target: BlockRegion;
    error: string;
    resetVersion: number;
    manifests: readonly BlockManifest[];
    choices: ReturnType<typeof preferredBlockCatalog>;
  } | null = null;
  search = '';
  category = '';
  target: BlockRegion = 'main';
  mobileRegion: BlockRegion = 'main';
  error = '';
  loading = false;
  undoMessage = '';
  private readonly historyLimit = 20;
  private undoHistory: DetailUndo[] = [];
  private textDraft: ComposerTextDraft | null = null;
  hasPendingChanges(): boolean {
    return this.textDraft !== null;
  }
  /** Describe only buffered text; applied layout and its Undo history stay owned here. */
  pendingEditorScopes(
    latestDocument: () => WorkflowDefinitionDocument | null = () => this.document,
  ): readonly WorkflowEditorPendingScope[] {
    const categories = [
      { id: 'staff-form-name', label: 'Staff page form name', matches: (target: ComposerTextTarget) => target.kind === 'form-title' },
      { id: 'staff-field-label', label: 'Staff page field label', matches: (target: ComposerTextTarget) => target.kind === 'capture-label' || target.kind === 'installed-label' },
      { id: 'staff-block-text', label: 'Staff page block text', matches: (target: ComposerTextTarget) => target.kind === 'block' },
    ];
    return categories.map(category => {
      const draft = () => this.textDraft && category.matches(this.textDraft.target) ? this.textDraft : null;
      const current = () => {
        const buffered = draft();
        const document = latestDocument();
        return buffered && document ? this.resolveTextTarget(document, buffered.nodeId, buffered.target) : null;
      };
      return {
        id: `${this.node.id}:${category.id}`,
        label: category.label,
        state: (): WorkflowEditorBufferState => {
          const buffered = draft();
          if (!buffered) return 'clean';
          const document = latestDocument(), resolved = current();
          if (!document || this.node.id !== buffered.nodeId ||
            document.workflowCode !== buffered.workflowCode ||
            document.definitionVersionCode !== buffered.definitionVersionCode ||
            JSON.stringify(document.packReference ?? null) !== buffered.packReference ||
            !resolved || resolved.pin !== buffered.pin || resolved.value !== buffered.original) return 'stale';
          if (this.disabled || this.loading || this.mode !== 'edit' ||
            (buffered.target.kind !== 'block' && !this.workEditorAvailable) || resolved.validate(buffered.value)) return 'invalid';
          return 'valid';
        },
        fingerprint: () => {
          const buffered = draft(), document = latestDocument(), resolved = current();
          return JSON.stringify({
            draft: buffered,
            owner: [this.node.id, this.node.typeCode, this.resetVersion],
            context: document ? [document.workflowCode, document.definitionVersionCode, document.packReference] : null,
            source: resolved && buffered ? [resolved.pin, resolved.value, resolved.validate(buffered.value)] : null,
            editable: [this.disabled, this.loading, this.mode, this.workEditorAvailable],
          });
        },
        apply: () => {
          const buffered = draft();
          if (!buffered) return true;
          if (buffered.target.kind !== 'block' && !this.workEditorAvailable) return false;
          const document = latestDocument();
          return !!document && this.applyPendingChanges(document);
        },
        discard: () => { if (draft()) this.discardPendingChanges(); },
        focus: () => { if (draft()) this.focusPendingChanges(); },
      };
    });
  }
  get quickRenameAvailable(): boolean {
    return this.workEditorAvailable && this.quickRenameTarget() !== null;
  }
  get quickRenameLabel(): string {
    return this.selection?.kind === 'form' ? 'Form name' : 'Field label';
  }
  get quickRenameValue(): string {
    if (this.textDraft && this.textDraft.target.kind !== 'block')
      return this.textDraft.value;
    const target = this.quickRenameTarget();
    return target
      ? (this.resolveTextTarget(this.document, this.node.id, target)?.value ??
          '')
      : '';
  }
  get quickRenameError(): string {
    return this.textDraft && this.textDraft.target.kind !== 'block'
      ? this.textDraft.error
      : '';
  }
  updateQuickRename(value: string): void {
    const target = this.quickRenameTarget();
    if (target && this.workEditorAvailable) this.updateTextDraft(target, value);
  }
  blockSettingValue(name: string): unknown {
    return this.textDraft?.target.kind === 'block' &&
      this.textDraft.target.field === name &&
      this.textDraft.target.id === this.selectedId
      ? this.textDraft.value
      : this.selected?.settings[name];
  }
  blockTextError(name: string): string {
    return this.textDraft?.target.kind === 'block' &&
      this.textDraft.target.field === name
      ? this.textDraft.error
      : '';
  }
  updateBlockText(name: string, value: string): void {
    if (this.selected)
      this.updateTextDraft(
        { kind: 'block', id: this.selected.instanceId, field: name },
        value,
      );
  }
  private quickRenameTarget(): ComposerTextTarget | null {
    const selection = this.selection;
    if (selection?.kind !== 'form' && selection?.kind !== 'field') return null;
    const command = this.selectedCommand;
    const contract = command ? this.contract(command) : null;
    if (!command || !contract) return null;
    const target: ComposerTextTarget =
      selection.kind === 'form'
        ? { kind: 'form-title', command: selection.command }
        : {
            kind: contract.supportsCaptureForm
              ? 'capture-label'
              : 'installed-label',
            command: selection.command,
            field: selection.id,
          };
    return this.resolveTextTarget(this.document, this.node.id, target)
      ? target
      : null;
  }
  private updateTextDraft(target: ComposerTextTarget, value: string): void {
    if (this.disabled || this.loading || this.mode !== 'edit') return;
    if (
      this.textDraft &&
      JSON.stringify(this.textDraft.target) !== JSON.stringify(target) &&
      !this.applyPendingChanges()
    )
      return;
    const resolved = this.resolveTextTarget(
      this.document,
      this.node.id,
      target,
    );
    if (!resolved) return;
    this.textDraft ??= {
      target,
      workflowCode: this.document.workflowCode,
      definitionVersionCode: this.document.definitionVersionCode,
      packReference: JSON.stringify(this.document.packReference ?? null),
      nodeId: this.node.id,
      pin: resolved.pin,
      original: resolved.value,
      value,
      error: '',
      ...(this.guardPageSettings || this.guardContentLibrary || this.guardNoticeSettings
        ? { pageSettingsNamespace: this.pageSettingsNamespace() }
        : {}),
    };
    this.textDraft.value = value;
    this.textDraft.error = resolved.validate(value);
    if (value === this.textDraft.original) this.textDraft = null;
    this.pendingChangesChange.emit(this.hasPendingChanges());
  }
  commitTextEdit(): boolean {
    return this.applyPendingChanges();
  }
  applyPendingChanges(latestDocument?: WorkflowDefinitionDocument): boolean {
    const draft = this.textDraft;
    const latest = latestDocument ?? this.document;
    const nodes = latest.nodes.filter(
      (node) => node?.id === (draft?.nodeId ?? this.node.id),
    );
    if (!draft) {
      if (nodes.length !== 1) return false;
      this.document = latest;
      this.node = nodes[0];
      this.synchronizeInputs();
      return true;
    }
    if (this.disabled || this.loading || this.mode !== 'edit')
      return this.failTextEdit(
        'This text cannot be applied while editing is unavailable.',
      );
    const resolved = this.resolveTextTarget(latest, draft.nodeId, draft.target);
    if (
      latest.workflowCode !== draft.workflowCode ||
      latest.definitionVersionCode !== draft.definitionVersionCode ||
      JSON.stringify(latest.packReference ?? null) !== draft.packReference ||
      nodes.length !== 1 ||
      !resolved ||
      resolved.pin !== draft.pin ||
      resolved.value !== draft.original
    )
      return this.failTextEdit(
        'This text or its saved source changed while you were editing. Cancel this edit and review the current settings.',
      );
    const error = resolved.validate(draft.value);
    if (error) return this.failTextEdit(error);
    const value =
      draft.target.kind === 'block' ? draft.value : draft.value.trim();
    this.document = latest;
    this.node = nodes[0];
    this.synchronizeInputs();
    if (value === resolved.value) {
      this.textDraft = null;
      this.pendingChangesChange.emit(false);
      return true;
    }
    this.recordUndo(resolved.label);
    const next = resolved.patch(value);
    this.textDraft = null;
    this.pendingChangesChange.emit(false);
    this.emitDocument(next);
    return true;
  }
  discardPendingChanges(): void {
    if (!this.textDraft) return;
    this.textDraft = null;
    this.pendingChangesChange.emit(false);
  }
  focusPendingChanges(): void {
    const target = this.textDraft?.target;
    setTimeout(() => {
      if (this.ownerViews.focusSuspended()) return;
      const fields = this.host.nativeElement.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement
      >('[data-staff-page-text]');
      Array.from(fields)
        .find((field) =>
          target?.kind === 'block'
            ? field.dataset['staffPageText'] === 'block' &&
              field.dataset['staffPageSetting'] === target.field
            : field.dataset['staffPageText'] === 'rename',
        )
        ?.focus({ preventScroll: true });
    });
  }
  onTextEditKeydown(event: Event, multiline = false): void {
    const key = event as KeyboardEvent;
    if (key.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.focusPendingChanges();
      this.discardPendingChanges();
      const control = event.target;
      if (
        control instanceof HTMLInputElement ||
        control instanceof HTMLTextAreaElement
      ) {
        const setting = control.dataset['staffPageSetting'];
        control.value =
          control.dataset['staffPageText'] === 'block' && setting
            ? String(this.blockSettingValue(setting) ?? '')
            : this.quickRenameValue;
      }
    } else if (
      key.key === 'Enter' &&
      (!multiline || key.ctrlKey || key.metaKey)
    ) {
      event.preventDefault();
      this.applyPendingChanges();
    }
  }
  private failTextEdit(error: string): false {
    if (this.textDraft) this.textDraft.error = error;
    this.focusPendingChanges();
    return false;
  }
  private prepareTextChange(): boolean {
    this.synchronizeInputs();
    return this.applyPendingChanges();
  }
  private resolveTextTarget(
    document: WorkflowDefinitionDocument,
    nodeId: string,
    target: ComposerTextTarget,
  ): ComposerTextResolution | null {
    const nodes = document.nodes.filter((node) => node?.id === nodeId);
    if (
      nodes.length !== 1 ||
      nodes[0].typeCode !== 'human_step' ||
      !nodes[0].declaration
    )
      return null;
    const node = nodes[0],
      declaration = node.declaration!;
    const named = (value: string) =>
      !value.trim()
        ? 'Enter a name of 1–120 characters.'
        : value.trim().length > 120
          ? 'Use a name of up to 120 characters.'
          : '';
    if (target.kind === 'block') {
      const matches = declaration.detail.blocks.filter(
        (block) => block.instanceId === target.id,
      );
      if (matches.length !== 1) return null;
      const block = matches[0],
        manifest = this.manifest(block),
        schema = manifest?.settingsSchema.fields[target.field];
      if (
        !manifest ||
        schema?.type !== 'string' ||
        (typeof block.settings[target.field] !== 'string' &&
          block.settings[target.field] !== undefined)
      )
        return null;
      return {
        value: block.settings[target.field] as string | undefined,
        pin: JSON.stringify([
          block.provider,
          block.code,
          block.version,
          block.settingsVersion,
        ]),
        label: `Change ${this.fieldLabel(target.field)}`,
        validate: (value) => {
          try {
            parseBlockSettings(
              {
                ...manifest.settingsSchema,
                fields: { [target.field]: schema },
              },
              { [target.field]: value },
            );
            return '';
          } catch {
            return `Use text of up to ${schema.maxLength} characters.`;
          }
        },
        patch: (value) =>
          replacePresentation(document, nodeId, {
            ...declaration,
            detail: {
              ...declaration.detail,
              blocks: declaration.detail.blocks.map((item) =>
                item.instanceId === block.instanceId
                  ? {
                      ...item,
                      settings: { ...item.settings, [target.field]: value },
                    }
                  : item,
              ),
            },
          }),
      };
    }
    const commands = declaration.behavior.commands.filter(
      (command) => this.formIdentity(command) === target.command,
    );
    if (commands.length !== 1) return null;
    const command = commands[0];
    const contracts =
        this.componentCatalog?.commands.filter((item) =>
          sameCapabilityReference(item.capability, command.capability),
        ) ?? [];
    if (contracts.length !== 1) return null;
    const contract = contracts[0];
    if ((isReviewRequestLookupCommand(command.capability) ? reviewRequestLookupSettingsAuthoringReason : preparationLookupSettingsAuthoringReason)({
      ownerKey: JSON.stringify([document.workflowCode, document.definitionVersionCode, document.packReference ?? null, nodeId]),
      document, nodeId, command, installed: contract, catalog: this.componentCatalog,
    }, command, contract)) return null;
    if (!isWorkflowFormVersionAvailable(document.payloadSchemaVersion, command.captureForm)
      || !isWorkflowFormVersionAvailable(document.payloadSchemaVersion, command.formConfiguration)) return null;
    const patch = (update: Partial<StepCommand>) =>
      replacePresentation(document, nodeId, {
        ...declaration,
        behavior: {
          ...declaration.behavior,
          commands: declaration.behavior.commands.map((item) =>
            item === command ? { ...item, ...update } : item,
          ),
        },
      });
    const pin = JSON.stringify([
      command.capability,
      command.captureForm?.id,
      contract.supportsCaptureForm,
      contract.form,
    ]);
    if (target.kind === 'form-title' || target.kind === 'capture-label') {
      const form = command.captureForm;
      if (
        !contract.supportsCaptureForm ||
        !form ||
        isUnsupportedCaptureForm(form)
      )
        return null;
      if (target.kind === 'form-title')
        return {
          value: form.title,
          pin,
          label: 'Rename form',
          validate: named,
          patch: (value) => patch({ captureForm: { ...form, title: value } }),
        };
      const fields = form.fields.filter((field) => field.id === target.field);
      return fields.length === 1 &&
        this.fieldTypes.some((type) => type.code === fields[0].type)
        ? {
            value: fields[0].label,
            pin,
            label: 'Rename field',
            validate: named,
            patch: (value) =>
              patch({
                captureForm: {
                  ...form,
                  fields: form.fields.map((field) =>
                    field.id === target.field
                      ? { ...field, label: value }
                      : field,
                  ),
                },
              }),
          }
        : null;
    }
    if (contract.supportsCaptureForm || isUnsupportedFormConfiguration(command))
      return null;
    if (command.formConfiguration?.version === 3 && providedFormSettingsAuthoringReason({
      ownerKey: JSON.stringify([document.workflowCode, document.definitionVersionCode, document.packReference ?? null, nodeId]),
      document, nodeId, command, installed: contract, catalog: this.componentCatalog,
    }, command, contract)) return null;
    const fields =
      contract.formFields?.filter((field) => field.name === target.field) ?? [];
    const saved =
      command.formConfiguration?.fields.filter(
        (field) => field.name === target.field,
      ) ?? [];
    if (fields.length !== 1 || saved.length > 1) return null;
    const field = fields[0],
      configuration = command.formConfiguration;
    return {
      value: saved[0]?.label ?? field.label,
      pin: JSON.stringify([command.capability, contract.form, field]),
      label: 'Rename field',
      validate: named,
      patch: (value) => {
        const setting = {
          ...saved[0],
          name: field.name,
          label: value,
          ...(!saved.length && field.control === 'select'
            ? {
                optionSource: field.optionSources.includes('step-outcomes')
                  ? ('step-outcomes' as const)
                  : ('contract-options' as const),
              }
            : {}),
        };
        return patch({
          formConfiguration: {
            ...configuration,
            version: configuration?.version ?? 1,
            fields: saved.length
              ? configuration!.fields.map((item) =>
                  item.name === field.name ? setting : item,
                )
              : [...(configuration?.fields ?? []), setting],
          },
        });
      },
    };
  }
  private currentNode = '';
  private currentDocument?: WorkflowDefinitionDocument;
  private currentNodeValue?: WorkflowDefinitionNode;
  private currentDocumentContent = '';
  private currentNodeContent = '';
  private currentResetVersion = 0;
  private readonly emptyDetail: Detail = {
    layout: 'workspace.two-column.v1',
    contextPlacement: 'side',
    blocks: [],
  };
  get detail(): Detail {
    return this.node.declaration?.detail ?? this.emptyDetail;
  }
  get staffActions(): StepCommand[] {
    const commands = this.node.declaration?.behavior?.commands;
    return (Array.isArray(commands) ? commands : []).filter(
      (command) =>
        noticeRecord(command) &&
        noticeRecord(command.capability) &&
        noticeRecord(command.capability.key) &&
        !isBlockOwnedCommand(command.capability.key),
    );
  }
  get selectedActionIdentity(): string {
    const target = this.selection;
    const command =
      target?.kind === 'action'
        ? this.staffActions.find(
            (item) => this.formIdentity(item) === target.command,
          )
        : null;
    return command ? this.commandIdentity(command) : '';
  }
  get selectedStaffAction(): StepCommand | undefined {
    return this.staffActions.find(
      (command) =>
        this.commandIdentity(command) === this.selectedActionIdentity,
    );
  }
  actionLabel(command: StepCommand): string {
    return this.contract(command)?.label ?? command.capability.key.code;
  }
  selectAction(command: StepCommand | null): void {
    this.configure(
      command
        ? { kind: 'action', command: this.formIdentity(command) }
        : { kind: 'action-library' },
    );
  }
  editActionForm(command: StepCommand): void {
    this.configure({ kind: 'form', command: this.formIdentity(command) });
  }
  applyActionDocument(document: WorkflowDefinitionDocument): void {
    if (this.hasPendingChanges()) {
      this.applyPendingChanges(document);
      return;
    }
    this.synchronizeInputs();
    if (
      this.disabled ||
      this.loading ||
      JSON.stringify(document) === JSON.stringify(this.document)
    )
      return;
    const before = this.staffActions.length;
    const after =
      document.nodes.find((node) => node.id === this.node.id)?.declaration
        ?.behavior.commands.length ?? 0;
    this.recordUndo(
      after > before
        ? 'Add staff action'
        : after < before
          ? 'Remove staff action'
          : 'Edit staff action',
      true,
    );
    this.emitDocument(document);
  }
  get blocks(): PlacedBlock[] {
    return this.detail.blocks;
  }
  get selected(): PlacedBlock | undefined {
    return this.blocks.find((b) => b.instanceId === this.selectedId);
  }
  get selectedManifest(): BlockManifest | null {
    return this.selected ? this.manifest(this.selected) : null;
  }
  trackField(_index: number, field: { name: string }): string {
    return field.name;
  }
  get fields() {
    return Object.entries(this.selectedManifest?.settingsSchema.fields ?? {})
      .filter(
        ([name]) =>
          !this.selectedDomainBlock ||
          ![
            'bindingMode',
            'referenceField',
            ...(this.selectedNoticeBlock
              ? ['noticeGroupId', 'policyJson']
              : []),
          ].includes(name),
      )
      .map(([name, schema]) => ({ name, schema }));
  }
  get selectedDomainBlock() {
    return !!this.selected && domainBlockCodes.has(this.selected.code);
  }
  private noticeView(
    document: WorkflowDefinitionDocument | null,
    blockId = this.selection?.kind === 'block' ? this.selection.id : null,
  ): NoticeView | null {
    if (!document || !blockId || !Array.isArray(document.nodes)) return null;
    const nodes = document.nodes.filter((node) => node?.id === this.node.id);
    if (nodes.length !== 1 || nodes[0].typeCode !== 'human_step') return null;
    const node = nodes[0],
      rawBlocks = node.declaration?.detail?.blocks;
    if (!Array.isArray(rawBlocks)) return null;
    const blocks = rawBlocks.filter(noticeRecord),
      matches = blocks.filter((block) => block.instanceId === blockId);
    if (
      matches.length !== 1 ||
      matches[0].provider !== 'notices' ||
      !noticeBlockCodes.has(matches[0].code)
    )
      return null;
    return {
      document,
      node,
      block: matches[0],
      blocks,
      notices: blocks.filter(
        (block) =>
          block.provider === 'notices' && noticeBlockCodes.has(block.code),
      ),
      commands: Array.isArray(node.declaration?.behavior?.commands)
        ? node.declaration.behavior.commands
        : [],
      requirements: Array.isArray(
        node.declaration?.behavior?.completionRequirements,
      )
        ? node.declaration.behavior.completionRequirements
        : [],
      pins: Array.isArray(document.capabilityReferences)
        ? document.capabilityReferences
        : [],
    };
  }
  private noticeGroupWitness(view: NoticeView): string {
    return JSON.stringify([
      this.pageSettingsNamespace(),
      view.node.id,
      view.block.instanceId,
      view.notices,
      view.commands.filter(
        (command) => command?.capability?.key?.provider === 'notices',
      ),
      view.pins.filter((pin) => pin?.key?.provider === 'notices'),
    ]);
  }
  private noticeRequirements(
    view: NoticeView,
  ): readonly {
    id: string;
    label: string;
    available: boolean;
    reason: string;
  }[] {
    const groupId = noticeRecord(view.block.settings)
      ? view.block.settings['noticeGroupId']
      : undefined;
    return view.requirements
      .filter((requirement) => requirement?.provider === NOTICE_REQUIREMENT)
      .map((requirement) => {
        const available =
          typeof requirement.id === 'string' &&
          !!requirement.id &&
          view.requirements.filter((row) => row?.id === requirement.id)
            .length === 1 &&
          requirement.version === 1 &&
          requirement.settingsVersion === 1 &&
          noticeRecord(requirement.settings) &&
          typeof groupId === 'string' &&
          !!groupId &&
          requirement.settings['noticeGroupId'] === groupId;
        return {
          id: requirement.id,
          label: 'Complete notice work',
          available,
          reason: available
            ? ''
            : 'This saved requirement is not one exact supported target for this notice group. Its original settings are retained.',
        };
      });
  }
  get noticeSettingsState(): {
    origin: WorkflowNoticeSettingsOrigin;
    name: string;
    unavailableReason: string;
    sourceSummary: string;
    sourceValue: string;
    sourceChoices: readonly { value: string; label: string }[];
    policySummary: string;
    policyJson: unknown;
    workLabel: string;
    workReason: string;
    canEnableWork: boolean;
    canMakeReadOnly: boolean;
    requirements: readonly {
      id: string;
      label: string;
      available: boolean;
      reason: string;
    }[];
    technical: unknown;
    refusal: { kind: 'remove' | 'read-only'; message: string } | null;
  } {
    const document = this.pageSettingsDocument(),
      view = this.noticeView(document);
    const origin: WorkflowNoticeSettingsOrigin = {
      ...this.pageSettingsState.origin,
      blockId: this.selection?.kind === 'block' ? this.selection.id : null,
      group: view ? this.noticeGroupWitness(view) : '',
      task: this.noticeSettingsOpen,
    };
    const result = {
      origin,
      name: view ? this.name(view.block) : 'Notice content settings',
      unavailableReason: '',
      sourceSummary: 'Saved source is unavailable.',
      sourceValue: '',
      sourceChoices: [] as { value: string; label: string }[],
      policySummary: '',
      policyJson: undefined as unknown,
      workLabel: 'Needs inspection',
      workReason: '',
      canEnableWork: false,
      canMakeReadOnly: false,
      requirements: view ? this.noticeRequirements(view) : [],
      technical: structuredClone(
        view
          ? {
              block: view.block,
              sharedNoticeContent: view.notices,
              requirements:
                view.node.declaration?.behavior?.completionRequirements,
              retainedStepCommands: view.node.declaration?.behavior?.commands,
              commands: view.commands.filter(
                (command) => command?.capability?.key?.provider === 'notices',
              ),
              pins: view.pins.filter((pin) => pin?.key?.provider === 'notices'),
            }
          : this.node?.declaration,
      ),
      refusal: this.noticeRefusal,
    };
    if (!view) {
      result.unavailableReason =
        'Select one exact saved notice block. Ambiguous or unavailable content remains unchanged.';
      return result;
    }
    try {
      const settings = view.block.settings;
      if (
        !noticeRecord(settings) ||
        view.block.code !== 'notices.delivery' ||
        view.block.version !== 2 ||
        view.block.settingsVersion !== 1 ||
        view.block.region !== 'main' ||
        view.block.width !== 'full'
      )
        throw new Error(
          'This focused editor supports Notice recipients and delivery version 2, Main content and Full row. The saved content is retained for inspection.',
        );
      const manifest = this.registry
        .catalog()
        .filter(
          (item) =>
            item.provider === view.block.provider &&
            item.code === view.block.code &&
            item.version === view.block.version &&
            item.settingsSchema.version === view.block.settingsVersion,
        );
      if (manifest.length !== 1)
        throw new Error('This exact Notice content contract is unavailable.');
      parseBlockSettings(manifest[0].settingsSchema, settings);
      const issue = noticeRequirementIssue(
        {
          noticeGroupId: settings['noticeGroupId'],
          policyJson: settings['policyJson'],
        },
        false,
      );
      if (issue) throw new Error(issue);
      if (
        view.requirements
          .filter((requirement) => requirement?.provider === NOTICE_REQUIREMENT)
          .some(
            (requirement) =>
              requirement.version !== 1 ||
              requirement.settingsVersion !== 1 ||
              !noticeRecord(requirement.settings) ||
              Object.keys(requirement.settings).some(
                (key) => !['noticeGroupId', 'policyJson'].includes(key),
              ) ||
              requirement.settings['noticeGroupId'] !==
                settings['noticeGroupId'] ||
              requirement.settings['policyJson'] !== settings['policyJson'],
          )
      )
        throw new Error(
          'The saved Notice requirement does not share this exact supported group policy. Inspect its owner in Work; no gate or policy is replaced automatically.',
        );
      if (
        !Array.isArray(view.node.declaration?.behavior?.commands) ||
        view.commands.some(
          (command) =>
            !noticeRecord(command) ||
            !noticeRecord(command.capability) ||
            !noticeRecord(command.capability.key),
        ) ||
        view.requirements.some((requirement) => !noticeRecord(requirement)) ||
        (view.node.declaration?.behavior?.completionRequirements != null &&
          !Array.isArray(
            view.node.declaration.behavior.completionRequirements,
          )) ||
        view.node.declaration?.detail.blocks.some(
          (block) => !noticeRecord(block),
        )
      )
        throw new Error(
          'Saved module declarations have an unavailable shape. Their original content is retained.',
        );
      const domains = view.blocks.filter((block) =>
        domainBlockCodes.has(block.code),
      );
      if (
        new Set(domains.map((block) => block.code)).size !== domains.length ||
        domains.some(
          (block) =>
            sourceContractVersion(block) !== 2 ||
            !noticeRecord(block.settings) ||
            block.settings['bindingMode'] !== settings['bindingMode'] ||
            block.settings['referenceField'] !== settings['referenceField'],
        ) ||
        view.notices.some(
          (block) =>
            !noticeRecord(block.settings) ||
            block.settings['noticeGroupId'] !== settings['noticeGroupId'] ||
            block.settings['policyJson'] !== settings['policyJson'],
        )
      )
        throw new Error(
          'This saved module group has inconsistent source or policy settings. Inspect its original content and use an explicit whole-group repair.',
        );
      const fields = view.document.processData?.fields;
      if (fields != null && !Array.isArray(fields))
        throw new Error('Saved shared values have an unavailable shape.');
      const hearingFields = (Array.isArray(fields) ? fields : []).filter(
        (field) =>
          field?.type === 'record-reference' &&
          field.recordTypeCode === 'scheduled-proceeding' &&
          typeof field.key === 'string' &&
          (fields ?? []).filter((row) => row?.key === field.key).length === 1 &&
          processDataErrors([field], { ...view.document, nodes: [] }).length ===
            0,
      );
      if (view.document.subjectTypeCode === 'hearing_cycle')
        result.sourceChoices.push({
          value: JSON.stringify(['linked-subject']),
          label: 'This workflow’s hearing',
        });
      result.sourceChoices.push(
        ...hearingFields.map((field) => ({
          value: JSON.stringify(['process-reference', field.key]),
          label: `${field.label} (${field.key})`,
        })),
      );
      result.sourceValue = JSON.stringify(
        settings['bindingMode'] === 'linked-subject'
          ? ['linked-subject']
          : ['process-reference', settings['referenceField']],
      );
      const selected = result.sourceChoices.filter(
        (choice) => choice.value === result.sourceValue,
      );
      if (selected.length !== 1)
        throw new Error(
          'The saved source is not one exact available Hearing reference. An Inmate reference cannot identify notice work.',
        );
      result.sourceSummary = selected[0].label;
      result.policyJson = structuredClone(settings['policyJson']);
      result.policySummary = noticePolicyDescription(settings['policyJson']);
      const commands = view.commands.filter(
        (command) => command?.capability?.key?.provider === 'notices',
      );
      let complete = true;
      if (
        commands.some(
          (command) =>
            !noticeCommandCodes.has(command.capability.key.code) ||
            command.capability.key.kind !== 'command' ||
            command.capability.key.version !== 1,
        )
      )
        throw new Error(
          'Unknown saved Notice commands remain inspectable. They cannot be removed or replaced by this editor.',
        );
      for (const code of noticeCommandCodes) {
        const matches = commands.filter(
          (command) => command.capability.key.code === code,
        );
        if (!matches.length) {
          complete = false;
          continue;
        }
        if (matches.length !== 1)
          throw new Error('Saved Notice commands have ambiguous identities.');
        const reference = matches[0].capability,
          pins = view.pins.filter(
            (pin) =>
              capabilityIdentity(pin.key) === capabilityIdentity(reference.key),
          );
        if (
          !reference.inputSchemaHash ||
          !reference.outputSchemaHash ||
          !reference.contractHash ||
          pins.length > 1 ||
          (pins.length === 1 && !sameCapabilityReference(pins[0], reference))
        )
          throw new Error(
            'A saved Notice command has an unavailable or different exact pin. No contract is replaced.',
          );
        if (!pins.length) complete = false;
      }
      for (const read of manifest[0].reads) {
        const pins = view.pins.filter(
          (pin) =>
            pin?.key?.provider === 'notices' &&
            pin.key.kind === 'view' &&
            pin.key.code === read.code &&
            pin.key.version === read.version,
        );
        if (
          pins.length > 1 ||
          pins.some(
            (pin) =>
              !pin.inputSchemaHash ||
              !pin.outputSchemaHash ||
              !pin.contractHash,
          )
        )
          throw new Error(
            'A saved Notice reader pin is ambiguous or unavailable.',
          );
        if (!pins.length) complete = false;
      }
      result.workLabel = complete
        ? 'Enabled'
        : commands.length
          ? 'Incomplete configuration'
          : 'Read-only';
      result.workReason = complete
        ? 'Saved Notice commands are configured. Runtime permissions and current completion evidence are checked separately.'
        : 'Enable notice work to check and add the exact installed operation dependencies.';
      result.canEnableWork = !complete;
      result.canMakeReadOnly = complete;
    } catch (error) {
      result.unavailableReason =
        error instanceof Error
          ? error.message
          : 'Saved Notice content is unavailable. Its original settings are retained.';
    }
    return result;
  }
  private noticeOriginCurrent(
    origin: WorkflowNoticeSettingsOrigin | undefined,
    mutation: boolean,
    logical = false,
  ): boolean {
    if (!this.guardNoticeSettings)
      return (
        !this.pageSettingsDestroyed &&
        this.active &&
        (!mutation || (!this.disabled && !this.loading))
      );
    const view = this.noticeView(this.pageSettingsDocument(), origin?.blockId),
      currentView = this.pageSettingsView?.() ?? null;
    return (
      !!origin &&
      !!view &&
      !this.pageSettingsDestroyed &&
      this.active &&
      !this.noticeRecoveryPending &&
      origin.task === this.noticeSettingsOpen &&
      (!mutation ||
        (!this.disabled &&
          (!this.loading || this.noticeEnableOrigin === origin) &&
          this.mode === 'edit' &&
          !this.noticeSettingsState.unavailableReason)) &&
      !!currentView &&
      currentView === this.pageSettingsRenderedView &&
      (this.pageSettingsViewState === undefined ||
        this.pageSettingsViewState === currentView) &&
      this.pageSettingsAuthority === this.pageSettingsNamespace() &&
      origin.generation === this.pageSettingsGeneration &&
      origin.owner === this.pageSettingsOwner() &&
      origin.nodeId === this.node.id &&
      origin.view === currentView &&
      origin.selection === JSON.stringify(this.selection) &&
      origin.source === JSON.stringify(this.pageSettingsDocument()) &&
      origin.source === JSON.stringify(this.document) &&
      origin.group === this.noticeGroupWitness(view) &&
      (!this.textDraft ||
        this.textDraft.pageSettingsNamespace ===
          this.pageSettingsNamespace()) &&
      !this.ownerViews.focusSuspended() &&
      (logical
        ? !this.noticeDialogs.openDialogs.some(
            (dialog) => dialog.getState() !== MatDialogState.CLOSED,
          ) && !this.host.nativeElement.closest('[hidden]')
        : !this.host.nativeElement.closest(
            '[hidden],[inert],[aria-hidden="true"]',
          ))
    );
  }
  private guardNoticeTask(origin?: WorkflowNoticeSettingsOrigin): boolean {
    if (origin !== undefined) return true;
    const blocks = this.node.declaration?.detail?.blocks,
      id = this.selection?.kind === 'block' ? this.selection.id : null;
    return (
      this.guardNoticeSettings &&
      Array.isArray(blocks) &&
      blocks.some(
        (block) =>
          block?.instanceId === id &&
          block.provider === 'notices' &&
          block.code === 'notices.delivery' &&
          block.version === 2 &&
          block.settingsVersion === 1,
      )
    );
  }
  private noticeOwnedCommands(): WorkflowCapabilityReference[] | null {
    const commands = this.node.declaration?.behavior?.commands;
    if (
      !Array.isArray(commands) ||
      commands.some(
        (command) =>
          !noticeRecord(command) ||
          !noticeRecord(command.capability) ||
          !noticeRecord(command.capability.key),
      )
    )
      return null;
    const notices = commands.filter(
      (command) => command?.capability?.key?.provider === 'notices',
    );
    const pins = Array.isArray(this.document.capabilityReferences)
      ? this.document.capabilityReferences
      : [];
    for (const command of notices) {
      const reference = command.capability,
        key = reference.key;
      if (
        key.kind !== 'command' ||
        key.version !== 1 ||
        !noticeCommandCodes.has(key.code) ||
        notices.filter(
          (item) =>
            capabilityIdentity(item.capability.key) === capabilityIdentity(key),
        ).length !== 1 ||
        !reference.inputSchemaHash ||
        !reference.outputSchemaHash ||
        !reference.contractHash
      )
        return null;
      const matching = pins.filter(
        (pin) =>
          pin?.key && capabilityIdentity(pin.key) === capabilityIdentity(key),
      );
      if (
        matching.length !== 1 ||
        !sameCapabilityReference(matching[0], reference)
      )
        return null;
    }
    return notices.map((command) => command.capability);
  }
  readonly noticePolicyContext = (): string | null => {
    const state = this.noticeSettingsState;
    return this.noticeSettingsOpen &&
      !state.unavailableReason &&
      this.noticeOriginCurrent(state.origin, true)
      ? JSON.stringify(state.origin)
      : null;
  };
  readonly currentNoticePolicy = (): unknown =>
    structuredClone(
      this.noticeView(this.pageSettingsDocument())?.block.settings?.[
        'policyJson'
      ],
    );
  openContentSettings(origin: WorkflowNoticeSettingsOrigin): void {
    if (!this.noticeOriginCurrent(origin, false) || !this.prepareTextChange())
      return;
    const current = this.noticeSettingsState.origin;
    if (!this.noticeOriginCurrent(current, false) || !current.blockId) return;
    const active = this.host.nativeElement.ownerDocument.activeElement;
    this.noticeSettingsReturn = {
      namespace: this.pageSettingsNamespace(),
      nodeId: this.node.id,
      blockId: current.blockId,
      tabId: this.previewTabId,
      origin:
        active instanceof HTMLElement &&
        this.host.nativeElement.contains(active)
          ? active
          : null,
    };
    this.noticeSettingsOpen = true;
    this.noticeRefusal = null;
    this.focusNoticeTask();
  }
  backToContentCanvas(origin: WorkflowNoticeSettingsOrigin): void {
    if (!this.noticeOriginCurrent(origin, false) || !this.prepareTextChange())
      return;
    const destination = this.noticeSettingsReturn;
    if (
      !destination ||
      destination.namespace !== this.pageSettingsNamespace() ||
      destination.nodeId !== this.node.id ||
      destination.blockId !== origin.blockId
    )
      return;
    this.noticeSettingsOpen = false;
    this.noticeRefusal = null;
    this.previewTabId = destination.tabId;
    this.focusNoticeTask(destination.origin);
  }
  private focusNoticeTask(origin?: HTMLElement | null): void {
    const state = this.noticeSettingsState.origin,
      request = ++this.ownerFocusRequest,
      open = this.noticeSettingsOpen,
      before = this.host.nativeElement.ownerDocument.activeElement;
    setTimeout(() => {
      const host = this.host.nativeElement,
        active = host.ownerDocument.activeElement;
      if (
        !host.isConnected ||
        request !== this.ownerFocusRequest ||
        open !== this.noticeSettingsOpen ||
        !this.noticeOriginCurrent(state, false)
      )
        return;
      if (
        active !== before &&
        active !== host.ownerDocument.body &&
        active?.isConnected &&
        !active.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      const target =
        !open &&
        origin?.isConnected &&
        !origin.closest('[hidden],[inert],[aria-hidden="true"]')
          ? origin
          : host.querySelector<HTMLElement>(
              open
                ? '[data-notice-settings-heading]'
                : '.selection-context__name',
            );
      if (
        !target?.isConnected ||
        target.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      target.focus({ preventScroll: true });
      if (
        host.ownerDocument.activeElement === target &&
        this.ownerFocusRequest === request + 1 &&
        this.noticeOriginCurrent(state, false)
      )
        target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }
  setNoticeSource(
    value: string,
    origin: WorkflowNoticeSettingsOrigin,
    model?: NgModel,
  ): void {
    const state = this.noticeSettingsState;
    if (
      value !== state.sourceValue &&
      state.sourceChoices.filter((choice) => choice.value === value).length ===
        1 &&
      this.prepareNoticeMutation(origin)
    ) {
      const selected: unknown = JSON.parse(value);
      if (Array.isArray(selected) && selected[0] === 'linked-subject')
        this.setDomainSource('linked-subject');
      else if (
        Array.isArray(selected) &&
        selected[0] === 'process-reference' &&
        typeof selected[1] === 'string'
      )
        this.setDomainSource('process-reference', selected[1]);
    }
    this.restorePageSettingsControl(
      model,
      this.noticeSettingsState.sourceValue,
    );
  }
  setNoticeBlockTab(
    id: string,
    origin: WorkflowNoticeSettingsOrigin,
    model?: NgModel,
  ): void {
    if (
      this.prepareNoticeMutation(origin) &&
      Array.isArray(this.detail.tabs) &&
      this.detail.tabs.filter((tab) => tab.id === id).length === 1
    ) {
      const effective = this.selected?.tabId ?? this.detail.tabs[0]?.id;
      if (id !== effective) this.setBlockTab(id);
    }
    const view = this.noticeView(this.pageSettingsDocument()),
      tabs = view?.node.declaration?.detail.tabs;
    this.restorePageSettingsControl(
      model,
      view?.block.tabId ?? (Array.isArray(tabs) ? tabs[0]?.id : undefined),
    );
  }
  moveNoticeBlock(delta: number, origin: WorkflowNoticeSettingsOrigin): void {
    if (this.prepareNoticeMutation(origin)) this.move(delta);
  }
  setNoticePolicyRequest(request: NoticePolicyChangeRequest): void {
    if (
      !this.noticePolicyEditor?.isCurrentPolicyChangeRequest(request) ||
      request.context !== this.noticePolicyContext()
    )
      return;
    this.setNoticePolicy(request.value, this.noticeSettingsState.origin);
  }
  private prepareNoticeMutation(
    origin: WorkflowNoticeSettingsOrigin | undefined,
  ): WorkflowNoticeSettingsOrigin | null {
    if (
      !origin ||
      this.noticeSettingsState.unavailableReason ||
      !this.noticeOriginCurrent(origin, true)
    )
      return null;
    const namespace = this.pageSettingsNamespace();
    if (!this.prepareTextChange() || namespace !== this.pageSettingsNamespace())
      return null;
    const current = this.noticeSettingsState.origin;
    if (
      current.owner !== origin.owner ||
      current.generation !== origin.generation ||
      current.view !== origin.view ||
      current.selection !== origin.selection ||
      current.blockId !== origin.blockId ||
      current.group !== origin.group ||
      current.task !== origin.task
    )
      return null;
    return this.noticeOriginCurrent(current, true) ? current : null;
  }
  private noticeRequirementContext(
    view: NoticeView,
    requirement: WorkflowCompletionRequirement,
  ): string {
    return JSON.stringify([this.noticeGroupWitness(view), requirement]);
  }
  isNoticeRequirementTargetCurrent(
    request: WorkflowNoticeRequirementRequest,
    current: WorkflowDefinitionDocument | null,
  ): boolean {
    if (
      !request ||
      request.owner !== this.pageSettingsOwner() ||
      request.nodeId !== this.node.id
    )
      return false;
    const view = this.noticeView(current, request.blockId);
    if (
      !view ||
      !this.noticeRequirements(view).some(
        (target) => target.available && target.id === request.requirementId,
      )
    )
      return false;
    const matches = view.requirements.filter(
      (requirement) => requirement?.id === request.requirementId,
    );
    return (
      matches.length === 1 &&
      view.block.settings['noticeGroupId'] === request.noticeGroupId &&
      JSON.stringify(matches[0]) === JSON.stringify(request.requirement) &&
      this.noticeRequirementContext(view, matches[0]) === request.context
    );
  }
  isCurrentNoticeRequirementRequest(
    request: WorkflowNoticeRequirementRequest,
    current: WorkflowDefinitionDocument | null = this.pageSettingsDocument(),
  ): boolean {
    return (
      this.noticeRequirementEmission === request &&
      !!this.noticeRequirementOrigin &&
      request.source === JSON.stringify(current) &&
      this.noticeOriginCurrent(
        this.noticeRequirementOrigin,
        false,
        this.noticeRequirementAfterDialog,
      ) &&
      this.isNoticeRequirementTargetCurrent(request, current)
    );
  }
  openNoticeRequirement(
    id: string,
    origin: WorkflowNoticeSettingsOrigin,
  ): void {
    this.emitNoticeRequirement(id, origin, false);
  }
  private emitNoticeRequirement(
    id: string,
    origin: WorkflowNoticeSettingsOrigin,
    afterDialog: boolean,
  ): void {
    if (
      !this.noticeOriginCurrent(origin, false, afterDialog) ||
      !this.prepareTextChange()
    )
      return;
    const view = this.noticeView(this.pageSettingsDocument(), origin.blockId);
    if (
      !view ||
      !this.noticeRequirements(view).some(
        (target) => target.id === id && target.available,
      )
    )
      return;
    const requirements = view.requirements.filter(
      (requirement) => requirement?.id === id,
    );
    if (
      requirements.length !== 1 ||
      typeof view.block.settings['noticeGroupId'] !== 'string' ||
      !origin.view
    )
      return;
    const request: WorkflowNoticeRequirementRequest = {
      nodeId: view.node.id,
      blockId: view.block.instanceId,
      noticeGroupId: view.block.settings['noticeGroupId'],
      requirementId: id,
      owner: origin.owner,
      source: JSON.stringify(view.document),
      view: origin.view,
      context: this.noticeRequirementContext(view, requirements[0]),
      requirement: structuredClone(requirements[0]),
    };
    this.noticeRequirementEmission = request;
    this.noticeRequirementOrigin = this.noticeSettingsState.origin;
    this.noticeRequirementAfterDialog = afterDialog;
    try {
      this.noticeRequirementRequested.emit(request);
    } finally {
      this.noticeRequirementEmission = null;
      this.noticeRequirementOrigin = null;
      this.noticeRequirementAfterDialog = false;
    }
  }
  keepNoticeContent(origin: WorkflowNoticeSettingsOrigin): void {
    if (!this.noticeOriginCurrent(origin, false)) return;
    if (this.error === this.noticeRefusal?.message) this.error = '';
    this.noticeRefusal = null;
  }
  private async refuseNoticeChange(
    kind: 'remove' | 'read-only',
    origin: WorkflowNoticeSettingsOrigin,
  ): Promise<void> {
    const message =
      kind === 'remove'
        ? 'Complete notice work depends on this content. Remove or change that requirement explicitly in Work before removing the delivery block.'
        : 'Complete notice work requires these evidence actions. Remove or change that requirement explicitly in Work before making notice work read-only.';
    this.error = message;
    this.noticeRefusal = { kind, message };
    const state = this.noticeSettingsState,
      targets = state.requirements.filter((target) => target.available),
      request = ++this.noticeRecoveryRequest;
    this.noticeRecoveryPending = true;
    let open: boolean;
    try {
      open = await this.confirmation.confirm({
        heading: 'Notice content is required',
        message,
        cancelText: 'Keep content',
        confirmText:
          targets.length === 1
            ? 'Open requirements'
            : 'Inspect content settings',
        tone: 'info',
      });
    } catch {
      if (request === this.noticeRecoveryRequest) {
        this.noticeRecoveryPending = false;
        this.error =
          'Notice feedback could not be opened. Your content and completion requirements are unchanged.';
      }
      return;
    }
    if (request !== this.noticeRecoveryRequest) return;
    this.noticeRecoveryPending = false;
    if (!this.noticeOriginCurrent(origin, false, true)) return;
    if (this.error === message) this.error = '';
    this.noticeRefusal = null;
    if (open && targets.length === 1)
      this.emitNoticeRequirement(targets[0].id, origin, true);
    else if (open) {
      this.noticeSettingsOpen = true;
      this.focusNoticeTask();
    }
  }
  get selectedNoticeBlock() {
    return !!this.selected && noticeBlockCodes.has(this.selected.code);
  }
  get noticeActionsEnabled() {
    return (
      this.node.declaration?.behavior.commands.some(
        (c) => c.capability.key.provider === 'notices',
      ) ?? false
    );
  }
  setNoticePolicy(policyJson: string, origin?: WorkflowNoticeSettingsOrigin) {
    if (
      this.guardNoticeTask(origin)
        ? !this.prepareNoticeMutation(origin)
        : !this.prepareTextChange()
    )
      return;
    this.synchronizeInputs();
    if (
      this.disabled ||
      this.loading ||
      !this.node.declaration ||
      !this.selectedNoticeBlock
    )
      return;
    if (!parseNoticePolicy(policyJson)) {
      this.error =
        'Choose at least one supported recipient rule and accepted response type.';
      return;
    }
    if (
      this.node.declaration.behavior.completionRequirements?.some(
        (r) => r.provider === NOTICE_REQUIREMENT,
      )
    ) {
      const issue = noticeRequirementIssue({
        noticeGroupId: this.selected!.settings['noticeGroupId'],
        policyJson,
      });
      if (issue) {
        this.error = issue;
        return;
      }
    }
    const next = replacePresentation(
      this.document,
      this.node.id,
      changeNoticePolicy(this.node.declaration, policyJson),
    );
    if (JSON.stringify(next) === JSON.stringify(this.document)) return;
    this.recordUndo('Change notice group policy', true);
    this.emitDocument(next);
  }
  async enableNoticeActions(origin?: WorkflowNoticeSettingsOrigin) {
    const guarded = this.guardNoticeTask(origin),
      guardedOrigin = guarded ? this.prepareNoticeMutation(origin) : null;
    if (guarded ? !guardedOrigin : !this.prepareTextChange()) return;
    if (
      this.disabled ||
      this.loading ||
      !this.selectedNoticeBlock ||
      !this.selected
    )
      return;
    const original = this.document,
      nodeId = this.node.id,
      settings = this.selected.settings;
    this.noticeEnableOrigin = guardedOrigin;
    this.loading = true;
    try {
      const catalog = await firstValueFrom(
        this.service.getCapabilities(original),
      );
      if (guardedOrigin && !this.noticeOriginCurrent(guardedOrigin, true))
        return;
      const next = ensureNoticeGroup(
        original,
        nodeId,
        {
          noticeGroupId: settings['noticeGroupId'],
          policyJson: settings['policyJson'],
        },
        catalog,
      );
      if (
        this.document !== original ||
        this.node.id !== nodeId ||
        this.disabled ||
        (guardedOrigin && !this.noticeOriginCurrent(guardedOrigin, true))
      )
        return;
      this.recordUndo('Enable notice actions', true);
      this.emitDocument(next);
    } catch (error) {
      if (guardedOrigin && !this.noticeOriginCurrent(guardedOrigin, false))
        return;
      this.error =
        error instanceof Error
          ? error.message
          : 'Notice actions are unavailable.';
    } finally {
      this.loading = false;
      if (this.noticeEnableOrigin === guardedOrigin)
        this.noticeEnableOrigin = null;
    }
  }
  disableNoticeActions(origin?: WorkflowNoticeSettingsOrigin) {
    const guarded = this.guardNoticeTask(origin),
      guardedOrigin = guarded ? this.prepareNoticeMutation(origin) : null;
    if (guarded ? !guardedOrigin : !this.prepareTextChange()) return;
    this.synchronizeInputs();
    if (this.disabled || this.loading || !this.node.declaration) return;
    if (
      this.node.declaration.behavior.completionRequirements?.some(
        (r) => r.provider === NOTICE_REQUIREMENT,
      )
    ) {
      if (guardedOrigin) {
        void this.refuseNoticeChange('read-only', guardedOrigin);
        return;
      }
      this.error =
        'Remove the notice completion requirement before disabling its evidence actions.';
      return;
    }
    const owned = this.noticeOwnedCommands();
    if (!owned) {
      this.error =
        'Saved Notice command ownership is unavailable or ambiguous. No commands or content were removed.';
      return;
    }
    if (!owned.length) return;
    this.recordUndo('Make notice work read-only', true);
    this.emitDocument(
      replacePresentation(this.document, this.node.id, {
        ...this.node.declaration,
        behavior: {
          ...this.node.declaration.behavior,
          commands: this.node.declaration.behavior.commands.filter(
            (c) =>
              !owned.some((reference) =>
                sameCapabilityReference(reference, c.capability),
              ),
          ),
        },
      }),
    );
  }
  get domainSource() {
    return sourceSettings(this.selected?.settings ?? {});
  }
  get hearingReferenceFields() {
    const generation = this.domainGeneration;
    return generation ? compatibleHearingFields(this.document, generation) : [];
  }
  get domainGeneration(): 1 | 2 | null {
    const block = this.blocks.find((item) => domainBlockCodes.has(item.code));
    try {
      return block ? sourceContractVersion(block) : 2;
    } catch {
      return null;
    }
  }
  upgradePreview: WorkflowDefinitionDocument | null = null;
  private upgradeOriginal: WorkflowDefinitionDocument | null = null;
  get sdmfRequired() {
    return (
      this.node.declaration?.behavior.completionRequirements?.some(
        (requirement) => requirement.provider === 'sdmf.completed',
      ) ?? false
    );
  }
  setDomainSource(
    mode: 'linked-subject' | 'process-reference',
    referenceField?: string,
  ) {
    if (!this.prepareTextChange()) return;
    this.synchronizeInputs();
    if (this.disabled || this.loading || !this.node.declaration) return;
    const selected =
      mode === 'process-reference'
        ? (referenceField ?? this.hearingReferenceFields[0]?.key)
        : undefined;
    if (
      mode === 'process-reference' &&
      !this.hearingReferenceFields.some((field) => field.key === selected)
    ) {
      this.error =
        'Declare a compatible Hearing or Inmate record reference before selecting this source.';
      return;
    }
    if (
      selected &&
      this.hearingReferenceFields.find((field) => field.key === selected)
        ?.recordTypeCode === 'incarcerated-person' &&
      this.blocks.some(
        (block) =>
          block.code === 'sdmf.analysis' || noticeBlockCodes.has(block.code),
      )
    ) {
      this.error =
        'SDMF and notice work require a Hearing reference field. An Inmate reference cannot select their proceeding.';
      return;
    }
    this.recordUndo('Change domain source', true);
    this.emitDocument(
      replacePresentation(
        {
          ...this.document,
          payloadSchemaVersion:
            mode === 'process-reference'
              ? requireWorkflowPayloadVersion(
                  this.document.payloadSchemaVersion,
                  '1.10',
                )
              : this.document.payloadSchemaVersion,
        },
        this.node.id,
        changeDomainSource(this.node.declaration, {
          bindingMode: mode,
          ...(selected ? { referenceField: selected } : {}),
        }),
      ),
    );
  }
  setSdmfRequired(required: boolean) {
    if (!this.prepareTextChange()) return;
    this.synchronizeInputs();
    if (this.disabled || this.loading || !this.node.declaration) return;
    try {
      const declaration = requireSdmf(
        this.node.declaration,
        required,
        this.componentCatalog?.requirementProviders?.find(
          (provider) =>
            provider.provider === 'sdmf.completed' &&
            provider.version ===
              this.blocks.find((block) => block.code === 'sdmf.analysis')
                ?.version,
        ),
      );
      this.recordUndo('Change required SDMF', true);
      this.emitDocument(
        replacePresentation(
          {
            ...this.document,
            payloadSchemaVersion: required
              ? requireWorkflowPayloadVersion(
                  this.document.payloadSchemaVersion,
                  '1.8',
                )
              : this.document.payloadSchemaVersion,
          },
          this.node.id,
          declaration,
        ),
      );
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : 'Unable to configure required SDMF.';
    }
  }
  private get capabilityCatalog(): WorkflowCapabilityCatalogEntry[] | null {
    // An empty optional input also represents the parent's not-yet-loaded state.
    // Only a completed fetch can establish that there really are no capabilities.
    return (
      this.fetchedCapabilities ??
      (this.componentCapabilities.length ? this.componentCapabilities : null)
    );
  }
  private get authoringCatalog() {
    const manifests = this.registry.catalog(),
      capabilities = this.capabilityCatalog;
    const cache = this.catalogCache;
    if (
      cache &&
      cache.document === this.document &&
      cache.node === this.node &&
      cache.capabilities === capabilities &&
      cache.target === this.target &&
      cache.error === this.catalogError &&
      cache.resetVersion === this.resetVersion &&
      cache.manifests.length === manifests.length &&
      cache.manifests.every((manifest, index) => manifest === manifests[index])
    )
      return cache.choices;
    const choices = preferredBlockCatalog(manifests, (manifest) =>
      this.authoringReason(manifest),
    );
    this.catalogCache = {
      document: this.document,
      node: this.node,
      capabilities,
      target: this.target,
      error: this.catalogError,
      resetVersion: this.resetVersion,
      manifests,
      choices,
    };
    return choices;
  }
  private matchesCatalogFilter(manifest: BlockManifest): boolean {
    return (
      (!this.category || manifest.category === this.category) &&
      this.registry
        .catalog(this.search)
        .some(
          (item) =>
            item.provider === manifest.provider &&
            item.code === manifest.code &&
            item.version === manifest.version,
        )
    );
  }
  get catalog(): BlockManifest[] {
    return this.authoringCatalog.available.filter((manifest) =>
      this.matchesCatalogFilter(manifest),
    );
  }
  get unavailableCatalog(): BlockManifest[] {
    return this.authoringCatalog.unavailable.filter((manifest) =>
      this.matchesCatalogFilter(manifest),
    );
  }
  get unavailableCount(): number {
    if (this.libraryOpen) {
      const search = (this.contentLibrary?.search() ?? this.search)
        .trim()
        .toLocaleLowerCase();
      const category = this.contentLibrary?.category() ?? this.category;
      return this.contentLibraryRows.filter(
        ({ entry }) =>
          !!entry.disabledReason &&
          (!category || entry.categoryId === category) &&
          (!search ||
            [entry.label, entry.description, entry.disabledReason].some(
              (value) => value?.toLocaleLowerCase().includes(search),
            )),
      ).length;
    }
    return this.unavailableCatalog.length;
  }
  catalogUnavailableReason(manifest: BlockManifest): string {
    return (
      this.authoringReason(manifest) ||
      'A newer compatible version is available.'
    );
  }
  get categories(): string[] {
    return [
      ...new Set(
        this.authoringCatalog.available.map((manifest) => manifest.category),
      ),
    ];
  }
  get commands() {
    return this.staffActions.filter(
      (command) =>
        !workflowCompleteCommandAdapter.matches(command.capability) &&
        this.componentCatalog?.interactions.some((interaction) =>
          sameCapabilityReference(interaction.command, command.capability),
        ),
    );
  }
  readonly commandIdentity = (command: {
    capability: WorkflowStepDeclaration['behavior']['commands'][number]['capability'];
  }) => capabilityIdentity(command.capability.key);
  get selectedInteractionIdentity() {
    return this.detail.mainInteraction
      ? `${this.unavailableInteraction ? 'unavailable:' : ''}${capabilityIdentity(this.detail.mainInteraction.command.key)}`
      : '';
  }
  get unavailableInteraction() {
    const selected = this.detail.mainInteraction;
    return (
      !!selected &&
      (!this.commands.some((command) =>
        sameCapabilityReference(command.capability, selected.command),
      ) ||
        !this.componentCatalog?.interactions.some(
          (interaction) =>
            interaction.code === selected.code &&
            interaction.version === selected.version &&
            sameCapabilityReference(interaction.command, selected.command),
        ))
    );
  }
  get canUndo(): boolean {
    return (
      !this.disabled &&
      !this.loading &&
      !this.hasPendingChanges() &&
      this.undoHistory.length > 0
    );
  }
  get undoLabel(): string {
    return this.undoHistory.at(-1)?.label ?? '';
  }
  ngOnChanges(): void {
    const settingsView = this.pageSettingsView?.() ?? null;
    if (
      (this.pageSettingsViewState === undefined ||
        this.pageSettingsViewState === settingsView) &&
      settingsView !== this.pageSettingsRenderedView
    ) {
      this.pageSettingsRenderedView = settingsView;
      this.pageSettingsGeneration++;
    }
    if (
      (this.pageSettingsViewState === undefined ||
        this.pageSettingsViewState === settingsView) &&
      JSON.stringify(this.pageSettingsDocument()) ===
        JSON.stringify(this.document)
    )
      this.pageSettingsAuthority = this.pageSettingsNamespace();
    if (
      this.noticeSettingsReturn &&
      (this.noticeSettingsReturn.namespace !== this.pageSettingsNamespace() ||
        this.noticeSettingsReturn.nodeId !== this.node.id ||
        this.selection?.kind !== 'block' ||
        this.selection.id !== this.noticeSettingsReturn.blockId)
    ) {
      this.noticeSettingsOpen = false;
      this.noticeSettingsReturn = null;
      this.noticeRefusal = null;
      this.noticeRecoveryRequest++;
      this.noticeRecoveryPending = false;
    }
    const viewKey = this.ownerViews.key(
      this.document,
      this.node.id,
      'staff-page',
    );
    const ownerChanged = viewKey !== this.ownerViewKey;
    const reactivated = this.active && !this.ownerActive;
    if (ownerChanged || this.active !== this.ownerActive)
      this.ownerFocusRequest++;
    const reset = this.currentResetVersion !== this.resetVersion;
    if (ownerChanged || reset || !this.active) this.rememberOwnerView();
    if (ownerChanged) {
      this.ownerViewKey = viewKey;
      this.ownerFocus = null;
      this.ownerFocusSelection = '';
    }
    const scope = this.capabilityScope();
    if (
      this.capabilitiesInput !== this.componentCapabilities ||
      this.catalogScope !== scope ||
      this.currentResetVersion !== this.resetVersion
    ) {
      this.capabilitiesInput = this.componentCapabilities;
      this.catalogScope = scope;
      this.fetchedCapabilities = null;
      this.catalogError = '';
    }
    this.synchronizeInputs();
    const restoring = ownerChanged || reset || this.ownerRestorePending;
    if (restoring) this.restoreOwnerView();
    if (
      this.active &&
      this.focusBlock !== this.lastFocusBlock &&
      this.focusBlock &&
      !this.ownerViews.hasConsumedFocus(this.ownerViewKey, this.focusBlock)
    ) {
      const blocks = this.blocks.filter(
        (b) => b.instanceId === this.focusBlock?.id,
      );
      if (
        blocks.length === 1 &&
        this.configure({ kind: 'block', id: blocks[0].instanceId })
      ) {
        this.lastFocusBlock = this.focusBlock;
        this.ownerViews.consumeFocus(this.ownerViewKey, this.focusBlock);
        this.focusProperties();
      }
    }
    if (
      this.active &&
      this.focusForm &&
      this.focusForm !== this.lastFocusForm &&
      !this.ownerViews.hasConsumedFocus(this.ownerViewKey, this.focusForm)
    ) {
      const commands = this.formCommands.filter((item) =>
        sameCapabilityReference(item.capability, this.focusForm!.capability),
      );
      // Catalog data arrives asynchronously. Retain the request until the exact form is available.
      if (
        commands.length === 1 &&
        this.configure({
          kind: 'form',
          command: this.formIdentity(commands[0]),
        })
      ) {
        this.lastFocusForm = this.focusForm;
        this.ownerViews.consumeFocus(this.ownerViewKey, this.focusForm);
      }
    }
    if (
      this.active &&
      this.componentCatalog &&
      this.focusAction &&
      !this.ownerViews.hasConsumedFocus(this.ownerViewKey, this.focusAction) &&
      this.focusAction !== this.lastFocusAction
    ) {
      const commands = this.staffActions.filter((item) =>
        sameCapabilityReference(item.capability, this.focusAction!.capability),
      );
      if (
        commands.length === 1 &&
        this.configure({
          kind: 'action',
          command: this.formIdentity(commands[0]),
        })
      ) {
        this.lastFocusAction = this.focusAction;
        this.ownerViews.consumeFocus(this.ownerViewKey, this.focusAction);
      }
    }
    this.ensureSelection();
    if (
      this.active &&
      (reactivated ||
        (restoring && !!this.ownerViews.read(this.ownerViewKey))) &&
      !this.ownerRestorePending
    )
      this.restoreOwnerFocus();
    this.ownerActive = this.active;
  }
  private synchronizeInputs(): void {
    const reset = this.currentResetVersion !== this.resetVersion;
    const changed =
      this.currentDocument !== this.document ||
      this.currentNodeValue !== this.node;
    if (!reset && !changed) return;
    const content = JSON.stringify(this.document);
    const nodeContent = JSON.stringify(this.node);
    const sameStep =
      this.currentNode === this.node.id &&
      (!this.currentDocument ||
        (this.currentDocument.workflowCode === this.document.workflowCode &&
          this.currentDocument.definitionVersionCode ===
            this.document.definitionVersionCode));
    // The parent clones emitted documents. Recognize that structural echo, but
    // never replay a layout snapshot across unrelated edits or an explicit reload.
    if (
      reset ||
      !sameStep ||
      content !== this.currentDocumentContent ||
      nodeContent !== this.currentNodeContent
    ) {
      this.undoHistory = [];
      this.undoMessage = '';
    }
    if (reset || !sameStep) {
      this.discardPendingChanges();
      this.selectedId = '';
      this.selection = null;
      this.selectionChosen = false;
      this.mode = 'edit';
      this.mobilePanel = 'page';
      this.inspectorOpen = true;
      this.libraryOpen = false;
      this.error = '';
    }
    this.rememberDocument(content, nodeContent);
    this.ensureSelection();
  }
  private rememberDocument(
    content = JSON.stringify(this.document),
    nodeContent = JSON.stringify(this.node),
  ): void {
    this.currentDocument = this.document;
    this.currentNodeValue = this.node;
    this.currentNode = this.node.id;
    this.currentDocumentContent = content;
    this.currentNodeContent = nodeContent;
    this.currentResetVersion = this.resetVersion;
  }
  private ensureSelection(): void {
    if (!this.blocks.some((b) => b.instanceId === this.selectedId)) {
      const selected = this.blocks[0];
      this.selectedId = selected?.instanceId ?? '';
      if (selected) this.mobileRegion = selected.region;
    }
    const selection = this.selection;
    if (
      selection?.kind === 'block' &&
      !this.blocks.some((b) => b.instanceId === selection.id)
    )
      this.selection = null;
    if (
      selection?.kind === 'action' &&
      !this.staffActions.some(
        (command) => this.formIdentity(command) === selection.command,
      )
    )
      this.selection = { kind: 'action-library' };
    const command = this.selectedCommand;
    if (
      (this.selection?.kind === 'field' || this.selection?.kind === 'form') &&
      !command
    )
      this.selection = null;
    if (
      !this.selectionChosen &&
      this.selection?.kind !== 'form' &&
      this.formCommands.length
    )
      this.selection = null;
    if (!this.selection) {
      const form =
        this.formCommands.find(
          (c) =>
            this.detail.mainInteraction &&
            sameCapabilityReference(
              c.capability,
              this.detail.mainInteraction.command,
            ),
        ) ?? this.formCommands[0];
      this.selection = form
        ? { kind: 'form', command: this.formIdentity(form) }
        : this.selectedId
          ? { kind: 'block', id: this.selectedId }
          : this.staffActions.length
            ? {
                kind: 'action',
                command: this.formIdentity(this.staffActions[0]),
              }
            : { kind: 'action-library' };
    }
  }
  readonly formIdentity = (command: StepCommand) =>
    workflowCommandIdentity(command.capability.key);
  contract(command: StepCommand) {
    return this.componentCatalog?.commands.find((item) =>
      sameCapabilityReference(item.capability, command.capability),
    );
  }
  get formCommands() {
    return this.staffActions.filter(
      (command) =>
        !workflowCompleteCommandAdapter.matches(command.capability) &&
        (!!this.contract(command)?.form ||
          this.contract(command)?.supportsCaptureForm),
    );
  }
  get selectedCommand() {
    const target = this.selection;
    return target?.kind === 'field' || target?.kind === 'form'
      ? this.formCommands.find((c) => this.formIdentity(c) === target.command)
      : undefined;
  }
  get selectedFieldId() {
    return this.selection?.kind === 'field' ? this.selection.id : null;
  }
  get selectionValue() {
    return JSON.stringify(this.selection);
  }
  get selectionOptions(): DetailSelectionOption[] {
    return [
      ...this.staffActions.map((command) => ({
        label: `${this.actionLabel(command)} · Action`,
        target: {
          kind: 'action' as const,
          command: this.formIdentity(command),
        },
      })),
      {
        label: 'Add staff action…',
        target: { kind: 'action-library' as const },
      },
      ...this.formCommands.flatMap((command) => {
        const id = this.formIdentity(command),
          contract = this.contract(command)!;
        const overrides = isUnsupportedFormConfiguration(command)
          ? []
          : (command.formConfiguration?.fields ?? []);
        const fields = contract.supportsCaptureForm
          ? (isUnsupportedCaptureForm(command.captureForm)
              ? []
              : (command.captureForm?.fields ?? [])
            ).map((f) => ({ id: f.id, label: f.label }))
          : (contract.formFields ?? []).map((f) => ({
              id: f.name,
              label:
                overrides.find((saved) => saved.name === f.name)?.label ??
                f.label,
            }));
        return [
          {
            label: command.captureForm?.title || contract.label,
            target: { kind: 'form' as const, command: id },
          },
          ...fields.map((field) => ({
            label: `↳ ${field.label || 'Untitled field'}`,
            target: { kind: 'field' as const, command: id, id: field.id },
          })),
        ];
      }),
      ...this.blocks.map((block) => ({
        label: this.name(block),
        target: { kind: 'block' as const, id: block.instanceId },
      })),
      { label: 'Page settings', target: { kind: 'tabs' as const } },
    ];
  }
  get selectionGroups(): {
    key: string;
    label: string;
    options: DetailSelectionOption[];
  }[] {
    const options = this.selectionOptions;
    return [
      ...this.staffActions.map((command) => {
        const identity = this.formIdentity(command);
        return {
          key: identity,
          label: `Action: ${this.actionLabel(command)}`,
          options: options
            .filter(
              (option) =>
                (option.target.kind === 'action' ||
                  option.target.kind === 'form' ||
                  option.target.kind === 'field') &&
                option.target.command === identity,
            )
            .map((option) => ({
              ...option,
              label:
                option.target.kind === 'action'
                  ? `Action settings: ${this.actionLabel(command)}`
                  : option.target.kind === 'form'
                    ? `Form: ${option.label}`
                    : `↳ Field: ${option.label.replace(/^↳ /, '')}`,
            })),
        };
      }),
      ...this.regions.map((region) => ({
        key: region,
        label: this.regionLabel(region),
        options: options
          .filter((option) =>
            option.target.kind === 'block'
              ? this.blocks.some(
                  (block) =>
                    block.instanceId ===
                      (
                        option.target as Extract<
                          DetailConfigureTarget,
                          { kind: 'block' }
                        >
                      ).id && block.region === region,
                )
              : option.target.kind === 'context' && region === 'supporting',
          )
          .map((option) => ({
            ...option,
            label:
              option.target.kind === 'block'
                ? `Block: ${option.label}`
                : option.label,
          })),
      })),
      {
        key: 'page-settings',
        label: 'Page settings',
        options: options.filter(option => option.target.kind === 'tabs'),
      },
      {
        key: 'add',
        label: 'Add to page',
        options: options.filter(
          (option) => option.target.kind === 'action-library',
        ),
      },
    ].filter((group) => group.options.length > 0);
  }
  get editingContext(): {
    type: string;
    label: string;
    parents: DetailSelectionParent[];
  } {
    const target = this.selection;
    if (target?.kind === 'tabs')
      return { type: 'Page settings', label: 'Page settings', parents: [] };
    if (target?.kind === 'action-library') {
      return { type: 'Staff actions', label: 'Add staff action', parents: [] };
    }
    if (target?.kind === 'context') {
      return {
        type: 'Page settings',
        label: 'Page settings',
        parents: [],
      };
    }
    if (target?.kind === 'block') {
      const block = this.blocks.find((item) => item.instanceId === target.id);
      return {
        type: block
          ? `Content block · ${this.regionLabel(block.region)}`
          : 'Content block',
        label: block ? this.name(block) : 'Unavailable block',
        parents: [],
      };
    }
    if (
      target?.kind === 'action' ||
      target?.kind === 'form' ||
      target?.kind === 'field'
    ) {
      const command = this.staffActions.find(
        (item) => this.formIdentity(item) === target.command,
      );
      const action = command ? this.actionLabel(command) : 'Unavailable action';
      if (target.kind === 'action')
        return { type: 'Staff action', label: action, parents: [] };
      const options = this.selectionOptions;
      const form = options.find(
        (option) =>
          option.target.kind === 'form' &&
          option.target.command === target.command,
      );
      const parents: DetailSelectionParent[] = [
        {
          type: 'Action',
          label: action,
          target: { kind: 'action', command: target.command },
        },
      ];
      if (target.kind === 'form')
        return {
          type: 'Form',
          label: form?.label ?? 'Unavailable form',
          parents,
        };
      parents.push({
        type: 'Form',
        label: form?.label ?? 'Unavailable form',
        target: { kind: 'form', command: target.command },
      });
      const field = options.find(
        (option) =>
          option.target.kind === 'field' &&
          option.target.command === target.command &&
          option.target.id === target.id,
      );
      return {
        type: 'Form field',
        label: field?.label.replace(/^↳ /, '') ?? 'Unavailable field',
        parents,
      };
    }
    return { type: 'Staff page', label: 'Choose an element', parents: [] };
  }
  readonly trackSelectionGroup = (_index: number, group: { key: string }) =>
    group.key;
  readonly trackSelection = (
    _index: number,
    option: { target: DetailConfigureTarget },
  ) => JSON.stringify(option.target);
  readonly trackIssue = (
    _index: number,
    issue: { target: DetailConfigureTarget },
  ) => JSON.stringify(issue.target);
  readonly targetValue = (target: DetailConfigureTarget) =>
    JSON.stringify(target);
  chooseSelection(value: string) {
    const option = this.selectionOptions.find(
      (item) => this.targetValue(item.target) === value,
    );
    if (option) this.configure(option.target);
  }
  chooseSelectionFromControl(value: string, control: HTMLSelectElement): void {
    this.chooseSelection(value);
    control.value = this.selectionValue;
  }
  editParent(target: DetailConfigureTarget): void {
    if (!this.configure(target)) return;
    setTimeout(() =>
      this.properties?.nativeElement
        .querySelector<HTMLElement>('.selection-context__name')
        ?.focus(),
    );
  }
  configure(target: DetailConfigureTarget): boolean {
    if (!this.prepareTextChange()) return false;
    if (target.kind === 'add-block') {
      if (this.mode === 'edit' && !this.disabled && !this.loading) {
        this.showLibrary(target.region, target.tabId);
      }
      return true;
    }
    this.selectionChosen = true;
    this.ownerRestorePending = false;
    this.selection = target;
    this.mode = 'edit';
    this.mobilePanel = 'settings';
    this.inspectorOpen = true;
    this.libraryOpen = false;
    if (target.kind === 'block') {
      this.selectedId = target.id;
      this.mobileRegion = this.selected?.region ?? 'main';
    }
    this.error = '';
    this.ensureSelection();
    return true;
  }
  configureFromCanvas(
    target: DetailConfigureTarget,
    origin?: WorkflowPageSettingsOrigin,
  ): void {
    if (target.kind === 'add-block') {
      this.showLibrary(target.region, target.tabId, origin);
      return;
    }
    if (!this.configure(target)) return;
    this.focusProperties();
  }
  private focusProperties(): void {
    setTimeout(() => {
      if (this.ownerViews.focusSuspended()) return;
      const panel = this.properties?.nativeElement;
      if (!panel) return;
      panel.scrollTop = 0;
      panel
        .querySelector<HTMLElement>('.selection-context__name')
        ?.focus({ preventScroll: true });
    });
  }
  setMobilePanel(panel: 'page' | 'settings'): void {
    if (!this.prepareTextChange()) return;
    this.mobilePanel = panel;
    if (panel === 'settings') {
      this.inspectorOpen = true;
      this.focusProperties();
    }
  }
  setInspector(open: boolean): void {
    if (!this.prepareTextChange()) return;
    this.inspectorOpen = open;
    this.mobilePanel = open ? 'settings' : 'page';
    if (open) this.focusProperties();
  }
  setMode(mode: 'edit' | 'try'): void {
    if (!this.prepareTextChange()) return;
    this.mode = mode;
    if (mode === 'try') this.mobilePanel = 'page';
    this.libraryOpen = false;
  }
  setCanvasSizeFromControl(value: string, control: HTMLSelectElement): void {
    if (
      this.prepareTextChange() &&
      (value === 'available' || value === 'phone')
    )
      this.canvasSize = value;
    control.value = this.canvasSize;
  }
  selectField(id: string | null) {
    const command = this.selectedCommand;
    if (command)
      this.configure(
        id === null
          ? { kind: 'form', command: this.formIdentity(command) }
          : { kind: 'field', command: this.formIdentity(command), id },
      );
  }
  get captureFormCopies() {
    return this.document.nodes
      .filter((node) => node.id !== this.node.id)
      .flatMap((node) =>
        (node.declaration?.behavior.commands ?? [])
          .filter((c) => c.captureForm)
          .map((c) => ({
            key: JSON.stringify([node.id, this.formIdentity(c)]),
            label: `${node.displayName || node.stepCode || node.id} · ${c.captureForm!.title || 'Untitled form'}`,
            form: c.captureForm!,
          })),
      );
  }
  get issues(): {
    label: string;
    message: string;
    target: DetailConfigureTarget;
  }[] {
    return this.formCommands.flatMap((command) => {
      const contract = this.contract(command)!,
        id = this.formIdentity(command);
      const validation = contract.supportsCaptureForm
        ? captureFormIssues(command.captureForm)
        : workflowFormFieldIssues(command, contract);
      return [
        ...(validation.form
          ? [
              {
                label: command.captureForm?.title || contract.label,
                message: validation.form,
                target: { kind: 'form' as const, command: id },
              },
            ]
          : []),
        ...Object.entries(validation.fields)
          .filter(([, message]) => !!message)
          .map(([field, message]) => ({
            label:
              command.captureForm?.fields?.find((f) => f.id === field)?.label ||
              command.formConfiguration?.fields?.find((f) => f.name === field)
                ?.label ||
              contract.formFields?.find((f) => f.name === field)?.label ||
              field,
            message,
            target: { kind: 'field' as const, command: id, id: field },
          })),
      ];
    });
  }
  get fieldIssues(): Record<string, string> {
    return Object.fromEntries(
      this.issues
        .filter((issue) => issue.target.kind === 'field')
        .map((issue) => {
          const target = issue.target as Extract<
            DetailConfigureTarget,
            { kind: 'field' }
          >;
          return [`${target.command}:${target.id}`, issue.message];
        }),
    );
  }
  get canAddField(): boolean {
    const form = this.selectedCommand?.captureForm;
    return !!form && !!this.selectedCommand && !this.formVersionUnavailable(this.selectedCommand)
      && !isUnsupportedCaptureForm(form) && form.fields.length < 50;
  }
  get formAtFieldLimit(): boolean {
    const form = this.selectedCommand?.captureForm;
    return (
      !!form && !isUnsupportedCaptureForm(form) && form.fields.length >= 50
    );
  }
  get selectedFormLabel(): string {
    const command = this.selectedCommand;
    return (
      command?.captureForm?.title ||
      (command ? this.actionLabel(command) : 'Selected form')
    );
  }
  addField(type: WorkflowCaptureField['type']) {
    if (!this.prepareTextChange()) return;
    if (
      this.disabled ||
      this.loading ||
      this.mode !== 'edit' ||
      !this.canAddField
    )
      return;
    if (this.workEditorAvailable) {
      const command = this.selectedCommand;
      const form = command?.captureForm;
      const definition = this.fieldTypes.find((field) => field.code === type);
      if (!command || !form || !definition) return;
      const field: WorkflowCaptureField = {
        id: `field-${crypto.randomUUID()}`,
        label: definition.label,
        type,
        required: false,
        ...(type === 'select' ? { options: [] } : {}),
      };
      this.changeCommand(
        command,
        { captureForm: { ...form, fields: [...form.fields, field] } },
        'Add field',
      );
      this.configure({
        kind: 'field',
        command: this.formIdentity(command),
        id: field.id,
      });
      setTimeout(() => {
        const input = this.host.nativeElement.querySelector<HTMLInputElement>(
          '[data-staff-page-text="rename"]',
        );
        input?.focus({ preventScroll: true });
        input?.select();
      });
      return;
    }
    this.captureEditor?.add(type);
    setTimeout(() =>
      this.properties?.nativeElement
        .querySelector<HTMLElement>('[data-field-setting="label"]')
        ?.focus(),
    );
  }
  setCaptureForm(command: StepCommand, form: WorkflowCaptureForm) {
    if (this.contract(command)?.supportsCaptureForm)
      this.changeCommand(command, { captureForm: form }, 'Edit form');
  }
  formVersionUnavailable(command: StepCommand): boolean {
    return isUnsupportedFormConfiguration(command) || !isWorkflowFormVersionAvailable(this.document.payloadSchemaVersion, command.captureForm)
      || !isWorkflowFormVersionAvailable(this.document.payloadSchemaVersion, command.formConfiguration);
  }
  setFormConfiguration(
    command: StepCommand,
    configuration: WorkflowFormConfiguration | null,
  ) {
    if (this.contract(command))
      this.changeCommand(
        command,
        { formConfiguration: configuration },
        'Edit field settings',
      );
  }
  private changeCommand(
    command: StepCommand,
    update: Partial<StepCommand>,
    label: string,
  ) {
    if (!this.prepareTextChange()) return;
    this.synchronizeInputs();
    if (this.disabled || this.loading) return;
    const contract = this.contract(command);
    if (isPreparationLookupCommand(command.capability) || isReviewRequestLookupCommand(command.capability)) {
      const unavailable = contract ? (isReviewRequestLookupCommand(command.capability) ? reviewRequestLookupSettingsAuthoringReason : preparationLookupSettingsAuthoringReason)({
        ownerKey: JSON.stringify([this.document.workflowCode, this.document.definitionVersionCode, this.document.packReference ?? null, this.node.id]),
        document: this.document, nodeId: this.node.id, command, installed: contract, catalog: this.componentCatalog,
      }, command, contract) || providedFormSettingsShapeReason(this.document, { ...command, ...update }, contract)
        : 'The exact installed action and form are unavailable. Its saved settings are preserved.';
      if (unavailable) { this.error = unavailable; return; }
    }
    const declaration = this.node.declaration!,
      identity = this.formIdentity(command);
    if (Object.hasOwn(update, 'captureForm') || Object.hasOwn(update, 'formConfiguration')) {
      const matching = declaration.behavior.commands.filter(item => this.formIdentity(item) === identity);
      if (matching.length !== 1 || JSON.stringify(matching[0]) !== JSON.stringify(command)) {
        this.error = 'This form changed before the edit could be applied. Its latest settings are preserved.';
        return;
      }
      if ([matching[0], { ...matching[0], ...update }].some(item => this.formVersionUnavailable(item))) {
        this.error = 'This saved form version is unavailable in this workflow format. Its settings are preserved.';
        return;
      }
      const next = { ...matching[0], ...update };
      if (next.captureForm && next.captureForm.version !== 1 && isUnsupportedCaptureForm(next.captureForm)
        || next.formConfiguration?.version === 2 && !!workflowFormFieldIssues(next, this.contract(command)!).form) {
        this.error = 'This form layout is unavailable or has invalid row groups. Its saved settings are preserved.';
        return;
      }
    }
    const commands = declaration.behavior.commands.map((item) =>
      this.formIdentity(item) === identity ? { ...item, ...update } : item,
    );
    if (
      JSON.stringify(commands) === JSON.stringify(declaration.behavior.commands)
    )
      return;
    this.error = '';
    this.recordUndo(label);
    this.emitDocument(
      replacePresentation(this.document, this.node.id, {
        ...declaration,
        behavior: { ...declaration.behavior, commands },
      }),
    );
  }
  reorderFields(event: DetailFieldOrder) {
    if (!this.prepareTextChange()) return;
    this.synchronizeInputs();
    const command = this.formCommands.find(
        (c) => this.formIdentity(c) === event.command,
      ),
      form = command?.captureForm;
    if (
      !command ||
      !this.contract(command)?.supportsCaptureForm ||
      !form ||
      isUnsupportedCaptureForm(form)
    )
      return;
    const fields = form.fields,
      ids = event.fields;
    if (
      ids.length !== fields.length ||
      new Set(ids).size !== fields.length ||
      ids.some((id) => !fields.some((f) => f.id === id))
    )
      return;
    const ordered = ids.map((id) => fields.find((f) => f.id === id)!);
    const invalid = ordered.find(
      (field, index) =>
        field.visibleWhen &&
        !ordered
          .slice(0, index)
          .some((earlier) => earlier.id === field.visibleWhen!.fieldId),
    );
    if (invalid) {
      this.error = `Keep “${fields.find((f) => f.id === invalid.visibleWhen!.fieldId)?.label || 'the controlling field'}” before “${invalid.label}”, which depends on it.`;
      return;
    }
    this.changeCommand(
      command,
      { captureForm: { ...form, fields: ordered } },
      'Reorder fields',
    );
  }
  moveBlock(event: DetailBlockMove) {
    if (!this.prepareTextChange()) return;
    this.synchronizeInputs();
    const block = this.blocks.find((item) => item.instanceId === event.id);
    if (block) this.relocate(block, event.region, event.index);
  }
  manifest(block: PlacedBlock): BlockManifest | null {
    try {
      return this.registry.resolve(block);
    } catch {
      return null;
    }
  }
  name(block: PlacedBlock): string {
    return this.manifest(block)?.name ?? `Unavailable block (${block.code})`;
  }
  regionLabel(region: BlockRegion): string {
    return region === 'main' ? 'Main content' : 'Reference rail';
  }
  inRegion(region: BlockRegion): PlacedBlock[] {
    return this.blocks.filter((b) => b.region === region);
  }
  fieldLabel(name: string): string {
    return (
      (
        {
          text: 'Guidance text',
          showNotes: 'Show review notes',
          showSchedulingLink: 'Show permitted scheduling link',
          bookingMode: 'Booking mode',
          startTime: 'Default start time', endTime: 'Default end time', timeZoneId: 'Booking time zone',
          locationName: 'Default hearing location', roomName: 'Default hearing room',
        } as Record<string, string>
      )[name] ?? name
    );
  }
  interactionLabel(identity: string): string {
    return (
      this.componentCatalog?.commands.find(
        (item) => capabilityIdentity(item.capability.key) === identity,
      )?.label ?? identity
    );
  }
  summary(block: PlacedBlock): string {
    return block.code === 'workflow.guidance'
      ? String(
          block.settings['text'] ||
            'Enter staff guidance in the properties below.',
        )
      : (this.manifest(block)?.description ??
          'Its saved configuration is preserved. Install this exact block version to edit it.');
  }
  get contentLibraryState(): { origin: WorkflowPageSettingsOrigin } {
    return { origin: this.pageSettingsState.origin };
  }
  private contentLibraryOriginCurrent(
    origin: WorkflowPageSettingsOrigin,
    modal: boolean,
  ): boolean {
    if (!this.guardContentLibrary)
      return !this.pageSettingsDestroyed && !this.disabled && this.active;
    const document = this.pageSettingsDocument(),
      view = this.pageSettingsView?.() ?? null;
    const ownLoading =
      modal && this.contentLibraryAdding === this.contentLibraryLease;
    return (
      !this.pageSettingsDestroyed &&
      !!document &&
      this.active &&
      !this.disabled &&
      this.mode === 'edit' &&
      (!this.loading || ownLoading) &&
      !!view &&
      view === this.pageSettingsRenderedView &&
      (this.pageSettingsViewState === undefined ||
        this.pageSettingsViewState === view) &&
      this.pageSettingsAuthority === this.pageSettingsNamespace() &&
      origin.generation === this.pageSettingsGeneration &&
      origin.nodeId === this.node.id &&
      origin.owner === this.pageSettingsOwner() &&
      origin.view === view &&
      origin.selection === JSON.stringify(this.selection) &&
      origin.source === JSON.stringify(document) &&
      origin.source === JSON.stringify(this.document) &&
      Array.isArray(document.nodes) &&
      document.nodes.filter((node) => node?.id === this.node.id).length === 1 &&
      document.nodes.some(
        (node) =>
          node.id === this.node.id &&
          node.typeCode === 'human_step' &&
          JSON.stringify(node) === JSON.stringify(this.node),
      ) &&
      (!this.textDraft ||
        this.textDraft.pageSettingsNamespace ===
          this.pageSettingsNamespace()) &&
      !this.ownerViews.focusSuspended() &&
      !this.host.nativeElement.closest(
        modal && this.contentLibraryDialogOpen
          ? '[hidden],[inert]'
          : '[hidden],[inert],[aria-hidden="true"]',
      )
    );
  }
  private contentLibraryCurrent(
    lease: ContentLibraryLease | null,
  ): lease is ContentLibraryLease {
    return (
      !!lease &&
      this.contentLibraryLease === lease &&
      lease.request === this.contentLibraryRequest &&
      lease.resetVersion === this.resetVersion &&
      lease.capabilities === this.componentCapabilities &&
      lease.region === this.target &&
      lease.tabId === this.targetTabId &&
      (lease.tabId === undefined ||
        (Array.isArray(this.detail.tabs) &&
          this.detail.tabs.filter((tab) => tab.id === lease.tabId).length ===
            1)) &&
      lease.origin.source === JSON.stringify(this.document) &&
      this.contentLibraryOriginCurrent(lease.origin, true)
    );
  }
  get contentLibraryContext(): string {
    const lease = this.contentLibraryLease;
    // Open state and cancellation are deliberately excluded: the shared selector
    // owns dismissal focus, while request invalidation separately fences late Add.
    return JSON.stringify([
      lease?.origin,
      lease?.region,
      lease?.tabId,
      lease?.resetVersion,
      this.pageSettingsOwner(),
      this.pageSettingsView?.() ?? null,
      JSON.stringify(this.pageSettingsDocument()),
    ]);
  }
  get contentLibraryActive(): boolean {
    const lease = this.contentLibraryLease;
    return (
      !!lease &&
      lease.resetVersion === this.resetVersion &&
      lease.capabilities === this.componentCapabilities &&
      lease.origin.source === JSON.stringify(this.document) &&
      this.contentLibraryOriginCurrent(lease.origin, true)
    );
  }
  get contentLibraryEntries(): readonly WorkflowLibraryEntry[] {
    return this.contentLibraryRows
      .filter((row) => this.showUnavailable || !row.entry.disabledReason)
      .map((row) => row.entry);
  }
  get contentLibraryCategories(): readonly WorkflowLibraryCategory[] {
    return [
      ...new Set(this.contentLibraryRows.map((row) => row.manifest.category)),
    ].map((id) => ({ id, label: id }));
  }
  contentLibraryDetails(entry: WorkflowLibraryEntry): BlockManifest | null {
    const rows = this.contentLibraryRows.filter(
      (row) => row.entry.id === entry.id,
    );
    return rows.length === 1 ? rows[0].manifest : null;
  }
  private contentLibraryIdentity(
    manifest: BlockManifest,
    catalog: readonly WorkflowCapabilityCatalogEntry[],
  ): string {
    const descriptors: WorkflowCapabilityCatalogEntry[] = [],
      seen = new Set<string>();
    const visit = (key: WorkflowCapabilityCatalogEntry['key']): void => {
      const identity = capabilityIdentity(key);
      if (seen.has(identity)) return;
      seen.add(identity);
      const matches = catalog.filter(
        (entry) => capabilityIdentity(entry.key) === identity,
      );
      if (matches.length !== 1)
        throw new Error(
          'A required content capability is unavailable or ambiguous.',
        );
      const entry = matches[0];
      installedReference(entry);
      if (
        typeof entry.module !== 'string' ||
        !entry.module.trim() ||
        !Array.isArray(entry.dependencies)
      )
        throw new Error('The installed content descriptor is unavailable.');
      descriptors.push(entry);
      for (const dependency of entry.dependencies) {
        const resolved = catalog.filter(
          (item) =>
            capabilityIdentity(item.key) === capabilityIdentity(dependency.key),
        );
        if (
          resolved.length !== 1 ||
          !sameCapabilityReference(installedReference(resolved[0]), dependency)
        )
          throw new Error(
            'An exact content dependency is unavailable or has changed.',
          );
        visit(dependency.key);
      }
    };
    for (const read of manifest.reads)
      visit({
        provider: manifest.provider,
        kind: 'view',
        code: read.code,
        version: read.version,
      });
    for (const code of blockOwnedCommandCodes(manifest))
      visit({
        provider: manifest.provider,
        kind: 'command',
        code,
        version: blockOwnedCommandVersion(manifest),
      });
    return JSON.stringify(['staff-content', 1, manifest, descriptors]);
  }
  private refreshContentLibraryRows(): void {
    const choices = this.authoringCatalog,
      available = new Set(choices.available);
    this.contentLibraryRows = [
      ...choices.available,
      ...choices.unavailable,
    ].map((manifest) => {
      let id: string,
        reason = available.has(manifest)
          ? ''
          : this.catalogUnavailableReason(manifest);
      try {
        id = this.contentLibraryIdentity(
          manifest,
          this.capabilityCatalog ?? [],
        );
      } catch (error) {
        id = JSON.stringify(['unavailable-staff-content', manifest]);
        reason =
          error instanceof Error
            ? error.message
            : 'The exact content capability is unavailable.';
      }
      return {
        manifest,
        entry: {
          id,
          label: manifest.name,
          description: `${manifest.description} ${manifest.keywords.join(' ')}`,
          categoryId: manifest.category,
          actionLabel: 'Add & configure',
          ...(reason ? { disabledReason: reason } : {}),
        },
      };
    });
  }
  private requireCurrentContentChoice(
    manifest: BlockManifest,
    id: string,
    catalog: readonly WorkflowCapabilityCatalogEntry[],
  ): void {
    const installed = this.registry
      .catalog()
      .filter(
        (item) =>
          item.provider === manifest.provider &&
          item.code === manifest.code &&
          item.version === manifest.version,
      );
    if (
      installed.length !== 1 ||
      !this.authoringCatalog.available.includes(installed[0]) ||
      this.contentLibraryIdentity(installed[0], catalog) !== id
    )
      throw new Error(
        'The exact content choice changed. Retry available content before adding it.',
      );
  }
  showLibrary(
    region: BlockRegion = this.target,
    tabId?: string,
    origin?: WorkflowPageSettingsOrigin,
  ): void {
    if (
      !this.contentLibraryOriginCurrent(
        origin ?? this.contentLibraryState.origin,
        false,
      ) ||
      (this.guardContentLibrary && !origin)
    )
      return;
    if (
      region === 'main' &&
      tabId !== undefined &&
      (!Array.isArray(this.detail.tabs) ||
        this.detail.tabs.filter((tab) => tab.id === tabId).length !== 1)
    )
      return;
    const owner = this.pageSettingsOwner(),
      namespace = this.pageSettingsNamespace();
    if (
      !this.prepareTextChange() ||
      owner !== this.pageSettingsOwner() ||
      namespace !== this.pageSettingsNamespace()
    )
      return;
    const tabs = this.detail.tabs;
    if (region === 'main' && tabs != null) {
      if (!Array.isArray(tabs)) return;
      if (
        tabId !== undefined &&
        tabs.filter((tab) => tab.id === tabId).length !== 1
      )
        return;
      tabId = [
        tabId,
        this.previewTabId,
        this.selected?.tabId,
        tabs[0]?.id,
      ].find(
        (id): id is string =>
          typeof id === 'string' &&
          tabs.filter((tab) => tab.id === id).length === 1,
      );
      if (tabs.length && tabId === undefined) return;
    } else {
      if (region === 'main' && tabId !== undefined) return;
      tabId = undefined;
    }
    this.target = region;
    this.targetTabId = tabId;
    const currentOrigin = this.contentLibraryState.origin;
    if (!this.contentLibraryOriginCurrent(currentOrigin, false)) return;
    this.contentLibraryFocus = null;
    this.contentLibraryLease = {
      origin: currentOrigin,
      region,
      tabId,
      resetVersion: this.resetVersion,
      capabilities: this.componentCapabilities,
      request: ++this.contentLibraryRequest,
    };
    this.refreshContentLibraryRows();
    this.libraryOpen = true;
    this.retryContentLibrary();
  }
  contentLibraryOpened(): void {
    this.contentLibraryDialogOpen = true;
    this.contentLibrary?.search.set(this.search);
    this.contentLibrary?.category.set(this.category);
  }
  contentLibraryClosing(_reason: WorkflowLibraryClosedReason): void {
    // Revoke insertion before the dialog's exit animation finishes. The later
    // closed callback still owns filter capture, cleanup and dismissal focus.
    this.libraryOpen = false;
    this.contentLibraryRequest++;
  }
  contentLibraryClosed(_reason: WorkflowLibraryClosedReason): void {
    if (this.contentLibrary && this.contentLibraryDialogOpen) {
      this.search = this.contentLibrary.search();
      this.category = this.contentLibrary.category();
    }
    this.libraryOpen = false;
    this.contentLibraryDialogOpen = false;
    this.contentLibraryRequest++;
    this.focusAddedContent();
  }
  private focusAddedContent(): void {
    const focus = this.contentLibraryFocus;
    this.contentLibraryFocus = null;
    if (!focus) return;
    setTimeout(() => {
      const host = this.host.nativeElement,
        document = host.ownerDocument,
        active = document.activeElement;
      if (
        !host.isConnected ||
        focus.request !== this.ownerFocusRequest ||
        focus.namespace !== this.pageSettingsNamespace() ||
        focus.resetVersion !== this.resetVersion ||
        !this.contentLibraryOriginCurrent(focus.origin, false)
      )
        return;
      if (
        active !== focus.before &&
        active !== document.body &&
        active?.isConnected &&
        !active.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      const heading = this.properties?.nativeElement.querySelector<HTMLElement>(
        '.selection-context__name',
      );
      if (
        !heading?.isConnected ||
        heading.closest('[hidden],[inert],[aria-hidden="true"]')
      )
        return;
      heading.focus({ preventScroll: true });
      if (
        document.activeElement === heading &&
        this.ownerFocusRequest === focus.request + 1 &&
        this.contentLibraryOriginCurrent(focus.origin, false)
      )
        heading.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    });
  }
  toggleContentLibraryUnavailable(): void {
    if (
      this.libraryOpen &&
      this.contentLibraryCurrent(this.contentLibraryLease)
    )
      this.showUnavailable = !this.showUnavailable;
  }
  retryContentLibrary(): void {
    const lease = this.contentLibraryLease;
    if (!this.libraryOpen || !this.contentLibraryCurrent(lease)) return;
    void this.loadCapabilityCatalog(true)
      .then(() => {
        if (this.contentLibraryCurrent(lease)) this.refreshContentLibraryRows();
      })
      .catch(() => undefined);
  }
  async chooseContentLibrary(id: string): Promise<void> {
    const lease = this.contentLibraryLease;
    if (
      !this.libraryOpen ||
      this.catalogLoading ||
      this.loading ||
      !this.contentLibraryCurrent(lease)
    )
      return;
    const rows = this.contentLibraryRows.filter((row) => row.entry.id === id);
    if (rows.length !== 1 || rows[0].entry.disabledReason) return;
    this.contentLibraryAdding = lease;
    try {
      await this.add(rows[0].manifest, { lease, id });
    } finally {
      if (this.contentLibraryAdding === lease) this.contentLibraryAdding = null;
    }
  }
  private capabilityScope(): string {
    return JSON.stringify([
      this.document?.workflowCode,
      this.document?.packReference,
    ]);
  }
  private async loadCapabilityCatalog(
    force = false,
  ): Promise<WorkflowCapabilityCatalogEntry[]> {
    if (!force && this.capabilityCatalog) return this.capabilityCatalog;
    const scope = this.capabilityScope();
    const lease = this.libraryOpen ? this.contentLibraryLease : null;
    if (
      this.catalogRequest &&
      this.catalogRequestScope === scope &&
      this.catalogRequestInput === this.componentCapabilities &&
      this.catalogRequestReset === this.resetVersion &&
      this.catalogRequestLease === lease
    )
      return this.catalogRequest;
    const input = this.componentCapabilities;
    const resetVersion = this.resetVersion;
    this.catalogLoading = true;
    this.catalogError = '';
    const request = firstValueFrom(this.service.getCapabilities(this.document))
      .then((catalog) => {
        if (
          this.componentCapabilities === input &&
          this.capabilityScope() === scope &&
          this.resetVersion === resetVersion &&
          (!lease || this.contentLibraryCurrent(lease))
        )
          this.fetchedCapabilities = catalog;
        return catalog;
      })
      .catch((error: unknown) => {
        if (
          this.catalogRequest === request &&
          (!lease || this.contentLibraryCurrent(lease))
        )
          this.catalogError =
            'Available content could not be checked. Close and reopen the library to try again.';
        throw error;
      })
      .finally(() => {
        if (this.catalogRequest === request) {
          this.catalogLoading = false;
          this.catalogRequest = null;
        }
      });
    this.catalogRequest = request;
    this.catalogRequestScope = scope;
    this.catalogRequestInput = input;
    this.catalogRequestReset = resetVersion;
    this.catalogRequestLease = lease;
    return request;
  }
  closeLibrary(): void {
    this.libraryOpen = false;
    this.contentLibraryRequest++;
    if (!this.contentLibraryDialogOpen) this.focusAddedContent();
  }
  @HostListener('keydown.escape') escape(): void {
    if (this.libraryOpen) this.contentLibrary?.dismiss();
  }
  async previewDomainUpgrade(): Promise<void> {
    if (!this.prepareTextChange()) return;
    if (
      this.disabled ||
      this.loading ||
      this.domainGeneration !== 1 ||
      !this.node.declaration
    )
      return;
    const original = this.document,
      nodeId = this.node.id;
    this.loading = true;
    this.error = '';
    try {
      const catalog = await firstValueFrom(
        this.service.getCapabilities(original),
      );
      let updated = original;
      const declaration = structuredClone(this.node.declaration);
      declaration.detail.blocks = declaration.detail.blocks.map((block) =>
        domainBlockCodes.has(block.code)
          ? { ...block, version: 2, settingsVersion: 2 }
          : block,
      );
      const group = declaration.detail.blocks.filter((block) =>
        domainBlockCodes.has(block.code),
      );
      for (const block of group) {
        const manifest = domainBlockManifests.find(
          (item) =>
            item.provider === block.provider &&
            item.code === block.code &&
            item.version === block.version,
        );
        if (!manifest)
          throw new Error('The exact upgraded block is unavailable.');
        for (const read of manifest.reads) {
          const entry = catalog.find(
            (item) =>
              item.key.provider === block.provider &&
              item.key.kind === 'view' &&
              item.key.code === read.code &&
              item.key.version === read.version,
          );
          if (!entry)
            throw new Error(
              'An exact upgraded read dependency is unavailable.',
            );
          updated = addWorkflowCapability(updated, entry, catalog);
        }
      }
      if (group.some((block) => block.code === 'sdmf.analysis')) {
        declaration.behavior.commands = declaration.behavior.commands.filter(
          (command) =>
            command.capability.key.provider !== 'sdmf' ||
            !isBlockOwnedCommand(command.capability.key),
        );
        for (const code of sdmfCommandCodes) {
          const entry = catalog.find(
            (item) =>
              item.key.provider === 'sdmf' &&
              item.key.kind === 'command' &&
              item.key.code === code &&
              item.key.version === 2,
          );
          if (!entry)
            throw new Error('An upgraded SDMF action is unavailable.');
          updated = addWorkflowCapability(updated, entry, catalog);
          declaration.behavior.commands.push({
            capability: updated.capabilityReferences!.find(
              (item) =>
                item.key.provider === 'sdmf' &&
                item.key.kind === 'command' &&
                item.key.code === code &&
                item.key.version === 2,
            )!,
            requirements: [],
            outcomes: [],
          });
        }
      }
      const required =
        declaration.behavior.completionRequirements?.some(
          (requirement) => requirement.provider === 'sdmf.completed',
        ) ?? false;
      const upgraded = required
        ? requireSdmf(
            declaration,
            true,
            this.componentCatalog?.requirementProviders?.find(
              (provider) =>
                provider.provider === 'sdmf.completed' &&
                provider.version === 2,
            ),
          )
        : declaration;
      updated = replacePresentation(updated, nodeId, upgraded);
      if (
        this.document !== original ||
        this.node.id !== nodeId ||
        this.disabled
      )
        return;
      this.upgradeOriginal = original;
      this.upgradePreview = updated;
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : 'The domain group cannot be upgraded.';
    } finally {
      this.loading = false;
    }
  }
  applyDomainUpgrade(): void {
    if (!this.prepareTextChange()) return;
    if (
      !this.upgradePreview ||
      this.document !== this.upgradeOriginal ||
      this.disabled ||
      this.loading
    ) {
      this.upgradePreview = null;
      return;
    }
    const updated = this.upgradePreview;
    this.upgradePreview = null;
    this.upgradeOriginal = null;
    this.recordUndo('Upgrade domain group to source contract 2', true);
    this.emitDocument(updated);
  }
  cancelDomainUpgrade(): void {
    this.upgradePreview = null;
    this.upgradeOriginal = null;
  }
  reason(manifest: BlockManifest, region = this.target): string {
    if (this.disabled || this.loading)
      return 'Editing is unavailable while another operation is pending.';
    return this.authoringReason(manifest, region);
  }
  private authoringReason(
    manifest: BlockManifest,
    region = this.target,
  ): string {
    const placement = this.placementReason(manifest, region);
    if (placement) return placement;
    if (
      (manifest.reads.length || blockOwnedCommandCodes(manifest).length) &&
      this.catalogError
    )
      return this.catalogError;
    try {
      return blockCapabilityReason(
        manifest,
        initializeBlankStep(this.document, this.node.id),
        this.capabilityCatalog,
      );
    } catch (error) {
      return error instanceof Error
        ? error.message
        : 'This step cannot accept new content yet.';
    }
  }
  private placementReason(
    manifest: BlockManifest,
    region = this.target,
  ): string {
    if (manifest.deprecated) return manifest.deprecated;
    const domain = this.blocks.find((block) =>
      domainBlockCodes.has(block.code),
    );
    if (
      noticeBlockCodes.has(manifest.code) &&
      !domain &&
      this.document.subjectTypeCode !== 'hearing_cycle' &&
      !compatibleHearingFields(this.document, 2).some(
        (f) => f.recordTypeCode === 'scheduled-proceeding',
      )
    )
      return 'Declare a Hearing reference before adding notice blocks to this step.';
    if (domainBlockCodes.has(manifest.code) && domain) {
      try {
        if (sourceContractVersion(domain) !== sourceContractVersion(manifest))
          return sourceContractVersion(domain) === 1
            ? 'Upgrade the whole domain group before adding a source-contract-2 block.'
            : 'This step uses source contract 2. Choose the version 2 block.';
      } catch {
        return 'This step retains an unavailable source version. Restore that version before adding related content.';
      }
    }
    if (
      (manifest.code === 'sdmf.analysis' ||
        noticeBlockCodes.has(manifest.code)) &&
      sourceSettings(domain?.settings ?? {}).referenceField &&
      this.document.processData?.fields.some(
        (field) =>
          field.key === sourceSettings(domain?.settings ?? {}).referenceField &&
          field.recordTypeCode === 'incarcerated-person',
      )
    )
      return 'SDMF and notice work require a Hearing reference field for this step.';
    if (region === 'supporting' && this.detail.contextPlacement === 'hidden')
      return 'Show the Reference rail before adding content here.';
    if (
      !manifest.placements.some(
        (p) => p.host === 'step-shell' && p.region === region,
      )
    )
      return `This content cannot be placed in ${this.regionLabel(region)}.`;
    if (
      manifest.instances === 'single-per-workspace' &&
      this.blocks.some(
        (b) => b.provider === manifest.provider && b.code === manifest.code,
      )
    )
      return 'Already in this workspace.';
    if (this.blocks.length >= 100)
      return 'This workspace has reached its block limit.';
    return '';
  }
  async add(
    manifest: BlockManifest,
    contentChoice?: { lease: ContentLibraryLease; id: string },
  ): Promise<void> {
    if (
      contentChoice &&
      (!this.contentLibraryCurrent(contentChoice.lease) || !this.libraryOpen)
    )
      return;
    if (!this.prepareTextChange()) return;
    this.synchronizeInputs();
    if (this.disabled || this.loading || this.placementReason(manifest)) return;
    const original = this.document,
      nodeId = this.node.id,
      resetVersion = this.resetVersion,
      capabilityInput = this.componentCapabilities,
      region = this.target;
    this.error = '';
    this.loading = true;
    try {
      let updated = initializeBlankStep(original, nodeId);
      if (manifest.reads.length || blockOwnedCommandCodes(manifest).length) {
        // Refresh at insertion as well as discovery: a module may have changed
        // after the library opened. Keep all work local until the closure passes.
        const catalog = await this.loadCapabilityCatalog(true);
        if (contentChoice) {
          if (
            !this.contentLibraryCurrent(contentChoice.lease) ||
            !this.libraryOpen
          )
            return;
          this.requireCurrentContentChoice(manifest, contentChoice.id, catalog);
        }
        const unavailable = blockCapabilityReason(manifest, updated, catalog);
        if (unavailable) throw new Error(unavailable);
        for (const read of manifest.reads) {
          const entry = catalog.find(
            (c) =>
              c.key.provider === manifest.provider &&
              c.key.kind === 'view' &&
              c.key.code === read.code &&
              c.key.version === read.version,
          );
          if (!entry)
            throw new Error(
              'The read required by this block is not installed. No draft changes were made.',
            );
          updated = addWorkflowCapability(updated, entry, catalog);
        }
        if (blockOwnedCommandCodes(manifest).length) {
          const declaration = updated.nodes.find(
            (node) => node.id === nodeId,
          )!.declaration!;
          const commands = [...declaration.behavior.commands];
          const provider = manifest.provider;
          for (const code of blockOwnedCommandCodes(manifest)) {
            const entry = catalog.find(
              (item) =>
                item.key.provider === provider &&
                item.key.kind === 'command' &&
                item.key.code === code &&
                item.key.version === blockOwnedCommandVersion(manifest),
            );
            if (!entry)
              throw new Error(
                'An action required by this content is unavailable. No draft changes were made.',
              );
            updated = addWorkflowCapability(updated, entry, catalog);
            const capability = updated.capabilityReferences!.find(
              (item) =>
                item.key.provider === provider &&
                item.key.kind === 'command' &&
                item.key.code === code &&
                item.key.version === blockOwnedCommandVersion(manifest),
            )!;
            if (
              !commands.some((command) =>
                sameCapabilityReference(command.capability, capability),
              )
            )
              commands.push({ capability, requirements: [], outcomes: [] });
          }
          updated = replacePresentation(updated, nodeId, {
            ...declaration,
            behavior: { ...declaration.behavior, commands },
          });
        }
      }
      if (
        this.disabled ||
        this.document !== original ||
        this.node.id !== nodeId ||
        this.resetVersion !== resetVersion ||
        this.componentCapabilities !== capabilityInput ||
        (contentChoice &&
          (!this.contentLibraryCurrent(contentChoice.lease) ||
            !this.libraryOpen ||
            this.contentLibraryIdentity(
              manifest,
              this.capabilityCatalog ?? [],
            ) !== contentChoice.id))
      )
        return;
      if (contentChoice)
        this.requireCurrentContentChoice(
          manifest,
          contentChoice.id,
          this.capabilityCatalog ?? [],
        );
      let number = 1;
      while (
        this.blocks.some((b) => b.instanceId === `${manifest.code}-${number}`)
      )
        number++;
      const settings: Record<string, unknown> = Object.fromEntries(
        Object.entries(manifest.settingsSchema.fields)
          .filter(([, v]) => v.required)
          .map(([key, v]) => [
            key,
            v.type === 'boolean'
              ? false
              : v.type === 'integer'
                ? v.minimum
                : v.type === 'enum'
                  ? v.values[0]
                  : '',
          ]),
      );
      if (manifest.provider === 'hearing') {
        for (const [key, code] of [
          ['quorumNodeId', 'voting.result'],
          ['sdmfNodeId', 'sdmf.analysis'],
          ['sourceNodeId', 'hearing.decision'],
        ]) {
          const sources = this.hearingSourceNodes(code);
          if (key in manifest.settingsSchema.fields && sources.length === 1)
            settings[key] = sources[0].id;
        }
      }
      if (
        manifest.provider === 'hearing' &&
        manifest.code === 'hearing.decision' &&
        manifest.version >= 2
      )
        settings['voteMeaning'] = 'grant-proposal';
      if (domainBlockCodes.has(manifest.code))
        Object.assign(
          settings,
          sourceSettings(
            this.blocks.find((item) => domainBlockCodes.has(item.code))
              ?.settings ?? {},
          ),
        );
      if (noticeBlockCodes.has(manifest.code)) {
        const existing = this.blocks.find((b) => noticeBlockCodes.has(b.code));
        settings['noticeGroupId'] =
          existing?.settings['noticeGroupId'] ??
          `notice-${crypto.randomUUID()}`;
        settings['policyJson'] =
          existing?.settings['policyJson'] ??
          JSON.stringify(defaultNoticePolicy());
        if (
          !this.blocks.some((b) => domainBlockCodes.has(b.code)) &&
          this.document.subjectTypeCode !== 'hearing_cycle'
        ) {
          settings['bindingMode'] = 'process-reference';
          settings['referenceField'] = compatibleHearingFields(
            this.document,
            2,
          ).find((f) => f.recordTypeCode === 'scheduled-proceeding')!.key;
        }
      }
      if (
        domainBlockCodes.has(manifest.code) &&
        settings['bindingMode'] === 'process-reference'
      )
        updated = {
          ...updated,
          payloadSchemaVersion: requireWorkflowPayloadVersion(
            updated.payloadSchemaVersion,
            '1.10',
          ),
        };
      const block: PlacedBlock = {
        instanceId: `${manifest.code}-${number}`,
        provider: manifest.provider,
        code: manifest.code,
        version: manifest.version,
        settingsVersion: manifest.settingsSchema.version,
        settings,
        region,
        width: 'full',
        ...(region === 'main' && this.detail.tabs?.length
          ? {
              tabId: contentChoice
                ? contentChoice.lease.tabId
                : ([
                    this.targetTabId,
                    this.previewTabId,
                    this.selected?.tabId,
                  ].find((id) =>
                    this.detail.tabs!.some((tab) => tab.id === id),
                  ) ?? this.detail.tabs[0].id),
            }
          : {}),
      };
      this.recordUndo(
        `Add ${manifest.name}`,
        manifest.provider === 'voting' ||
          manifest.code === 'sdmf.analysis' ||
          noticeBlockCodes.has(manifest.code),
      );
      this.selectionChosen = true;
      this.selection = { kind: 'block', id: block.instanceId };
      this.selectedId = block.instanceId;
      this.mobileRegion = region;
      this.emitDocument(
        replacePresentation(updated, nodeId, {
          ...updated.nodes.find((node) => node.id === nodeId)!.declaration!,
          detail: { ...this.detail, blocks: [...this.blocks, block] },
        }),
      );
      this.mobilePanel = 'settings';
      this.inspectorOpen = true;
      if (contentChoice) {
        this.contentLibraryFocus = {
          origin: this.contentLibraryState.origin,
          namespace: this.pageSettingsNamespace(),
          resetVersion: this.resetVersion,
          request: this.ownerFocusRequest,
          before: this.host.nativeElement.ownerDocument.activeElement,
        };
      }
      this.closeLibrary();
      if (!contentChoice) this.focusProperties();
    } catch (error) {
      if (contentChoice && !this.contentLibraryCurrent(contentChoice.lease))
        return;
      this.error =
        error instanceof Error ? error.message : 'Unable to add this block.';
      if (contentChoice && this.contentLibraryCurrent(contentChoice.lease)) {
        this.catalogError = this.error;
        this.refreshContentLibraryRows();
      }
    } finally {
      this.loading = false;
    }
  }
  get compactRecordGridUpgrade(): BlockManifest | null {
    const selected = this.selected;
    if (!selected || !domainBlockCodes.has(selected.code)) return null;
    try {
      if (sourceContractVersion(selected) !== 2) return null;
    } catch { return null; }
    return headerHistoryBlockManifests.find((manifest) =>
      manifest.provider === selected.provider && manifest.code === selected.code && manifest.version > selected.version,
    ) ?? null;
  }
  async upgradeRecordGrid(): Promise<void> {
    await this.upgradeBlock(this.compactRecordGridUpgrade, 'Use compact record grid');
  }
  get blockSizingUpgrade(): BlockManifest | null {
    const selected = this.selected, current = this.selectedManifest;
    if (!selected || !current || selected.version !== 1 || selected.settingsVersion !== 1) return null;
    try { parseBlockSettings(current.settingsSchema, selected.settings); } catch { return null; }
    return resizableSchedulingBlockManifests.find((manifest) =>
      manifest.provider === selected.provider && manifest.code === selected.code && manifest.version === 2,
    ) ?? null;
  }
  get blockWidthChoices(): readonly PlacedBlock['width'][] {
    const selected = this.selected, manifest = this.selectedManifest;
    if (!selected || !manifest) return [];
    try { parseBlockSettings(manifest.settingsSchema, selected.settings); } catch { return []; }
    return (this.blockSizingUpgrade ?? manifest).layout.widths;
  }
  get schedulingBookingUpgrade(): BlockManifest | null {
    return this.selected?.provider === 'scheduling' && this.selected.code === 'scheduling.hearing' && this.selected.version < 3
      ? schedulingBlockManifests.find(m => m.code === 'scheduling.hearing' && m.version === 3) ?? null : null;
  }
  async enableBookingOptions(): Promise<void> {
    await this.upgradeBlock(this.schedulingBookingUpgrade, 'Enable staff-page booking options');
  }
  get combinedNoticeUpgrade(): BlockManifest | null {
    return this.selected?.provider === 'notices' && this.selected.code === 'notices.delivery' && this.selected.version === 1
      ? domainBlockManifests.find(m => m.code === 'notices.delivery' && m.version === 2) ?? null : null;
  }
  async combineNoticeBlocks(): Promise<void> {
    await this.upgradeBlock(this.combinedNoticeUpgrade, 'Combine notice recipients and delivery', true);
  }
  private async upgradeBlock(manifest: BlockManifest | null, label: string, combineNotices = false, requestedWidth?: PlacedBlock['width']): Promise<void> {
    if (!this.prepareTextChange() || this.disabled || this.loading) return;
    const selected = this.selected;
    if (!manifest || !selected) return;
    const original = this.document, nodeId = this.node.id, resetVersion = this.resetVersion;
    const sizingOrigin = this.pageSettingsState.origin, sizingNamespace = this.pageSettingsNamespace();
    const sizingCurrent = () => this.active && !this.disabled && this.mode === 'edit' && !this.pageSettingsDestroyed
      && JSON.stringify(this.pageSettingsState.origin) === JSON.stringify(sizingOrigin)
      && sizingNamespace === this.pageSettingsNamespace()
      && sizingOrigin.source === JSON.stringify(original) && sizingOrigin.source === JSON.stringify(this.document)
      && !this.ownerViews.focusSuspended() && !this.host.nativeElement.closest('[hidden],[inert],[aria-hidden="true"]');
    if (requestedWidth !== undefined && (manifest !== this.blockSizingUpgrade || !manifest.layout.widths.includes(requestedWidth)
      || !this.pageSettingsCurrent(sizingOrigin, false) || !sizingCurrent() || this.pageSettingsShapeReason())) return;
    this.loading = true;
    this.error = '';
    try {
      const catalog = await this.loadCapabilityCatalog(true);
      const reason = blockCapabilityReason(manifest, original, catalog);
      if (reason) throw new Error(reason);
      let updated = original;
      for (const read of manifest.reads) {
        const entry = catalog.find((item) => item.key.provider === manifest.provider && item.key.kind === 'view'
          && item.key.code === read.code && item.key.version === read.version)!;
        updated = addWorkflowCapability(updated, entry, catalog);
      }
      if (this.disabled || this.document !== original || this.node.id !== nodeId
        || this.resetVersion !== resetVersion || this.selected !== selected
        || requestedWidth !== undefined && !sizingCurrent()) return;
      this.recordUndo(label, true);
      const declaration = updated.nodes.find((node) => node.id === nodeId)!.declaration!;
      // Only consolidate a duplicate roster on this same page with identical governed bindings.
      const duplicateRoster = (block: PlacedBlock) => combineNotices && block.provider === 'notices' && block.code === 'notices.recipients'
        && block.region === selected.region && block.tabId === selected.tabId
        && ['bindingMode', 'referenceField', 'noticeGroupId', 'policyJson'].every(key => block.settings[key] === selected.settings[key]);
      this.emitDocument(replacePresentation(updated, nodeId, {
        ...declaration,
        detail: { ...declaration.detail, blocks: declaration.detail.blocks.filter(block => !duplicateRoster(block)).map((block) => block.instanceId === selected.instanceId
          ? { ...block, version: manifest.version, settingsVersion: manifest.settingsSchema.version,
              settings: requestedWidth !== undefined ? block.settings
                : { ...(manifest.code === 'scheduling.hearing' && manifest.version === 3 ? { bookingMode: 'calendar' } : {}), ...block.settings },
              ...(requestedWidth !== undefined ? { width: requestedWidth } : {}) }
          : block) },
      }));
    } catch (error) {
      if (requestedWidth !== undefined && !sizingCurrent()) return;
      this.error = error instanceof Error ? error.message : 'This block version is unavailable.';
    } finally { this.loading = false; }
  }

  private recordUndo(label: string, wholeDocument = false): void {
    this.undoHistory.push({
      document:
        wholeDocument || !this.node.declaration
          ? structuredClone(this.document)
          : null,
      detail: structuredClone(this.detail),
      commands: structuredClone(this.node.declaration?.behavior.commands ?? []),
      selection: this.selection ? structuredClone(this.selection) : null,
      selectedId: this.selectedId,
      mobileRegion: this.mobileRegion,
      label,
    });
    if (this.undoHistory.length > this.historyLimit) this.undoHistory.shift();
    this.undoMessage = '';
  }
  applyVotingDocument(document: WorkflowDefinitionDocument): void {
    if (this.hasPendingChanges()) {
      this.applyPendingChanges(document);
      return;
    }
    if (this.disabled || this.loading) return;
    this.recordUndo('Configure voting work', true);
    this.emitDocument(document);
  }
  private emitDocument(document: WorkflowDefinitionDocument): void {
    // Advance before emitting: multiple controls can change before Angular
    // returns the parent's updated input, and every edit must use the latest one.
    this.document = document;
    this.node = document.nodes.find((node) => node.id === this.node.id)!;
    this.rememberDocument();
    this.ensureSelection();
    this.documentChange.emit(document);
  }
  private change(detail: Detail, label: string): void {
    if (!this.disabled && !this.loading) {
      if (JSON.stringify(detail) === JSON.stringify(this.detail)) return;
      this.error = '';
      const withTabs = !!detail.tabs?.length;
      const version = withTabs ? requireWorkflowPayloadVersion(this.document.payloadSchemaVersion, '1.18') : this.document.payloadSchemaVersion;
      this.recordUndo(label, version !== this.document.payloadSchemaVersion);
      const document = { ...initializeBlankStep(this.document, this.node.id), payloadSchemaVersion: version };
      this.emitDocument(
        replacePresentation(document, this.node.id, {
          ...document.nodes.find((node) => node.id === this.node.id)!
            .declaration!,
          detail,
        }),
      );
    }
  }
  undo(): void {
    this.synchronizeInputs();
    if (!this.canUndo) return;
    const previous = this.undoHistory.pop()!;
    this.selection = previous.selection;
    this.selectedId = previous.selectedId;
    this.mobileRegion = previous.mobileRegion;
    this.error = '';
    this.emitDocument(
      previous.document ??
        replacePresentation(this.document, this.node.id, {
          ...this.node.declaration!,
          detail: previous.detail,
          behavior: {
            ...this.node.declaration!.behavior,
            commands: previous.commands,
          },
        }),
    );
    this.undoMessage = `Undid: ${previous.label}. Save draft to keep this change.`;
  }
  setContext(value: Detail['contextPlacement'], origin?: WorkflowPageSettingsOrigin): void {
    if (!this.pageSettingsCurrent(origin, true) || this.guardPageSettings && !['side', 'below', 'hidden'].includes(value) || !this.prepareTextChange()) return;
    this.synchronizeInputs();
    if (value === 'hidden' && this.inRegion('supporting').length) {
      this.error =
        'Move or remove the context blocks before hiding their region.';
      return;
    }
    this.change(
      { ...this.detail, contextPlacement: value },
      'Change context placement',
    );
  }
  setContextFromControl(
    value: Detail['contextPlacement'],
    control: HTMLSelectElement,
    origin?: WorkflowPageSettingsOrigin,
    model?: NgModel,
  ): void {
    this.setContext(value, origin);
    control.value = this.detail.contextPlacement;
    this.restorePageSettingsControl(model, this.detail.contextPlacement);
  }
  setInteraction(code: string): void {
    if (!this.prepareTextChange()) return;
    this.synchronizeInputs();
    const command = this.commands.find((c) => this.commandIdentity(c) === code);
    const interaction =
      command &&
      this.componentCatalog?.interactions.find((item) =>
        sameCapabilityReference(item.command, command.capability),
      );
    if (code && !interaction) {
      this.error =
        'This exact form contract is unavailable. Its saved settings are preserved.';
      return;
    }
    this.change(
      {
        ...this.detail,
        mainInteraction: command
          ? {
              code: interaction!.code,
              version: interaction!.version,
              command: command.capability,
            }
          : null,
      },
      'Change page presentation',
    );
  }
  hearingSourceNodes(code: string) {
    return this.document.nodes.filter(
      (n) =>
        (n.id !== this.node.id || code === 'voting.result') &&
        n.declaration?.detail.blocks.some((b) => code.startsWith('voting.') ? providesVotingBlock(b, code) : b.code === code),
    );
  }
  setInteractionFromControl(value: string, control: HTMLSelectElement): void {
    this.setInteraction(value);
    control.value = this.selectedInteractionIdentity;
  }
  setting(name: string, value: unknown): void {
    if (!this.prepareTextChange()) return;
    this.synchronizeInputs();
    const selected = this.selected,
      manifest = this.selectedManifest;
    if (!selected || !manifest) return;
    const settings = { ...selected.settings, [name]: value };
    try {
      parseBlockSettings(manifest.settingsSchema, settings);
      this.update({ ...selected, settings }, `Change ${this.fieldLabel(name)}`);
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : 'Invalid block setting.';
    }
  }
  update(block: PlacedBlock, label = 'Change block settings'): void {
    if (!this.prepareTextChange()) return;
    this.synchronizeInputs();
    this.change(
      {
        ...this.detail,
        blocks: this.blocks.map((b) =>
          b.instanceId === block.instanceId ? block : b,
        ),
      },
      label,
    );
  }
  async width(value: PlacedBlock['width']): Promise<void> {
    if (!this.pageSettingsCurrent(this.pageSettingsState.origin, false) || !this.prepareTextChange()) return;
    this.synchronizeInputs();
    const selected = this.selected;
    if (!selected || selected.width === value || !this.blockWidthChoices.includes(value)) return;
    const upgrade = this.blockSizingUpgrade;
    if (upgrade && !this.selectedManifest?.layout.widths.includes(value)) {
      await this.upgradeBlock(upgrade, 'Change block width', false, value);
      return;
    }
    this.update({ ...selected, width: value }, 'Change block width');
  }
  async widthFromControl(
    value: PlacedBlock['width'],
    control: HTMLSelectElement,
  ): Promise<void> {
    const change = this.width(value);
    control.value = this.selected?.width ?? '';
    await change;
    control.value = this.selected?.width ?? '';
  }
  canPlace(block: PlacedBlock, region: BlockRegion): boolean {
    return (
      !!this.manifest(block)?.placements.some(
        (p) => p.host === 'step-shell' && p.region === region,
      ) &&
      !(region === 'supporting' && this.detail.contextPlacement === 'hidden')
    );
  }
  place(region: BlockRegion): void {
    if (!this.prepareTextChange()) return;
    this.synchronizeInputs();
    if (this.selected)
      this.relocate(this.selected, region, this.inRegion(region).length);
  }
  placeFromControl(value: BlockRegion, control: HTMLSelectElement): void {
    this.place(value);
    control.value = this.selected?.region ?? '';
  }
  move(delta: number): void {
    if (!this.prepareTextChange()) return;
    this.synchronizeInputs();
    if (!this.selected) return;
    if (this.detail.tabs?.length && this.selected.region === 'main') {
      const siblings = this.tabSiblings(this.selected), index = siblings.findIndex(block => block.instanceId === this.selectedId);
      const other = siblings[index + delta];
      if (!other) return;
      const blocks = [...this.blocks], from = blocks.indexOf(this.selected), to = blocks.indexOf(other);
      [blocks[from], blocks[to]] = [blocks[to], blocks[from]];
      this.change({ ...this.detail, blocks }, 'Move block');
      return;
    }
    this.relocate(
      this.selected,
      this.selected.region,
      this.inRegion(this.selected.region).findIndex(
        (b) => b.instanceId === this.selectedId,
      ) + delta,
    );
  }
  canMove(delta: number): boolean {
    const s = this.selected;
    if (!s) return false;
    const siblings = this.tabSiblings(s), i = siblings.findIndex(b => b.instanceId === s.instanceId) + delta;
    return i >= 0 && i < siblings.length;
  }
  private tabSiblings(block: PlacedBlock): PlacedBlock[] {
    const fallback = this.detail.tabs?.[0]?.id;
    return this.inRegion(block.region).filter(item => block.region !== 'main' || !fallback || (item.tabId ?? fallback) === (block.tabId ?? fallback));
  }
  drop(event: CdkDragDrop<PlacedBlock[]>, region: BlockRegion): void {
    if (!this.prepareTextChange()) return;
    this.synchronizeInputs();
    const current = this.blocks.find(
      (block) =>
        block.instanceId === (event.item.data as PlacedBlock).instanceId,
    );
    if (current) this.relocate(current, region, event.currentIndex);
  }
  private relocate(
    block: PlacedBlock,
    region: BlockRegion,
    index: number,
  ): void {
    if (!this.canPlace(block, region)) {
      this.error = 'This block cannot be placed in that region.';
      return;
    }
    const target = this.inRegion(region).filter(
      (b) => b.instanceId !== block.instanceId,
    );
    if (index < 0 || index > target.length) return;
    const moved = { ...block, region };
    if (region === 'supporting') delete moved.tabId;
    else if (this.detail.tabs?.length && !this.detail.tabs.some(tab => tab.id === moved.tabId)) moved.tabId = this.detail.tabs[0].id;
    target.splice(index, 0, moved);
    const other = this.blocks.filter(
      (b) => b.region !== region && b.instanceId !== block.instanceId,
    );
    this.change(
      {
        ...this.detail,
        blocks:
          region === 'main' ? [...target, ...other] : [...other, ...target],
      },
      'Move block',
    );
    this.mobileRegion = region;
  }
  remove(origin?: WorkflowNoticeSettingsOrigin): void {
    const guarded = this.guardNoticeTask(origin),
      guardedOrigin = guarded ? this.prepareNoticeMutation(origin) : null;
    if (guarded ? !guardedOrigin : !this.prepareTextChange()) return;
    this.synchronizeInputs();
    if (this.selectedNoticeBlock && !this.noticeOwnedCommands()) {
      this.error =
        'Saved Notice command ownership is unavailable or ambiguous. No commands or content were removed.';
      return;
    }
    if (this.selected?.provider === 'voting') {
      if (this.disabled || this.loading) return;
      try {
        const next = removeVoting(
          this.document,
          this.node.id,
          this.selected.code,
        );
        this.recordUndo('Remove voting block', true);
        this.emitDocument(next);
      } catch (error) {
        this.error =
          error instanceof Error
            ? error.message
            : 'The voting block could not be removed.';
      }
      return;
    }
    if (
      this.selected?.code === 'notices.delivery' &&
      this.node.declaration?.behavior.completionRequirements?.some(
        (r) => r.provider === NOTICE_REQUIREMENT,
      )
    ) {
      if (guardedOrigin) {
        void this.refuseNoticeChange('remove', guardedOrigin);
        return;
      }
      this.error =
        'Remove the notice completion requirement before removing its delivery block. The completion gate is preserved.';
      return;
    }
    if (
      this.selectedNoticeBlock &&
      this.node.declaration &&
      this.blocks.filter((b) => noticeBlockCodes.has(b.code)).length === 1 &&
      !this.disabled &&
      !this.loading
    ) {
      const owned = this.noticeOwnedCommands();
      if (!owned) {
        this.error =
          'Saved Notice command ownership is unavailable or ambiguous. No commands or content were removed.';
        return;
      }
      this.recordUndo('Remove notice group', true);
      this.emitDocument(
        replacePresentation(this.document, this.node.id, {
          ...this.node.declaration,
          detail: {
            ...this.detail,
            blocks: this.blocks.filter((b) => b.instanceId !== this.selectedId),
          },
          behavior: {
            ...this.node.declaration.behavior,
            commands: this.node.declaration.behavior.commands.filter(
              (c) =>
                !owned.some((reference) =>
                  sameCapabilityReference(reference, c.capability),
                ),
            ),
          },
        }),
      );
      return;
    }
    if (this.selected?.code === 'sdmf.analysis' && this.sdmfRequired) {
      this.error =
        'Turn off “Require completed SDMF” before removing this block. Removing the block cannot silently remove its completion gate.';
      return;
    }
    if (
      this.selected?.code === 'sdmf.analysis' &&
      this.node.declaration &&
      !this.disabled &&
      !this.loading
    ) {
      this.recordUndo('Remove SDMF analysis', true);
      this.emitDocument(
        replacePresentation(this.document, this.node.id, {
          ...this.node.declaration,
          detail: {
            ...this.detail,
            blocks: this.blocks.filter(
              (block) => block.instanceId !== this.selectedId,
            ),
          },
          behavior: {
            ...this.node.declaration.behavior,
            commands: this.node.declaration.behavior.commands.filter(
              (command) =>
                command.capability.key.provider !== 'sdmf' ||
                !isBlockOwnedCommand(command.capability.key),
            ),
          },
        }),
      );
      return;
    }
    if (this.selected)
      this.change(
        {
          ...this.detail,
          blocks: this.blocks.filter((b) => b.instanceId !== this.selectedId),
        },
        `Remove ${this.name(this.selected)}`,
      );
  }
}
